import React, { useEffect, useState, createElement, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Loader2, Copy, Check, Code, ChevronDown, ChevronUp, X, Settings, HistoryIcon } from 'lucide-react';
import { LayoutTemplateIcon, SearchIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { signupFormsApi, formConfigsApi, emailTemplatesApi, newslettersApi, extractLookupId, formTemplatesLibraryApi, FormTemplateRecord, landingPagesApi, LandingPageRecord, formVersionsApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditService } from '../services/auditService';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
import { FormBuilder } from '../components/builder/FormBuilder';
import { VersionHistoryPanel } from '../components/VersionHistoryPanel';
import { ViewCodeModal } from '../components/ViewCodeModal';
import { FormBlock, FormTheme, FormBuilderSchema, FormLogicRule, DEFAULT_FORM_THEME, LANDING_PAGE_PALETTE } from '../components/builder/types';
/** Convert a string to a URL-safe slug */
function toSlug(str: string): string {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}
/** Escape a string for safe embedding inside single-quoted JS strings */
function escapeForJSString(str: string): string {
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/<\/script>/gi, '<\\/script>');
}
/** Data baked into the embed script from linked Form Config / Email Template / Newsletter */
interface EnrichmentData {
  emailHtml: string;
  emailSubject: string;
  newsletterId: string;
  region: string;
  redirectUrl: string;
  appOrigin: string;
  rules?: Array<{
    field: string;
    operator: string;
    value: string;
    html: string;
    subject: string;
  }>;
  confirmationLanguage?: string;
}
/** Fetch linked email template + newsletter data for a given signup form ID */
export async function fetchEnrichmentData(formId: string): Promise<EnrichmentData | null> {
  console.log('[fetchEnrichmentData] START — formId:', formId);
  try {
    // 1. Find Form Configuration linked to this form
    const configs = await formConfigsApi.findByFormId(formId);
    console.log('[fetchEnrichmentData] Step 1 — Form Configs found:', configs.length, configs.map(c => ({
      ID: c.ID,
      Email_Template: c.Email_Template,
      Team: c.Team
    })));
    if (configs.length === 0) {
      console.warn('[fetchEnrichmentData] ❌ RETURNING NULL — No Form Configuration linked to form', formId);
      return null;
    }
    const config = configs[0];
    const templateId = extractLookupId(config.Email_Template);
    console.log('[fetchEnrichmentData] Step 2 — templateId extracted:', templateId, '(raw Email_Template:', config.Email_Template, ')');
    // 2. Fetch the Default Email Template
    let template = null;
    if (templateId) {
      template = await emailTemplatesApi.get(templateId);
      console.log('[fetchEnrichmentData] Step 2 — Template fetched:', template ? {
        subject: template.Email_Subject,
        htmlLength: template.Email_HTML_Body?.length
      } : 'NOT FOUND');
    } else {
      console.log('[fetchEnrichmentData] Step 2 — No templateId, skipping template fetch');
    }
    // 3. Fetch the Signup Form to get conditional rules from Builder_Schema
    const formRecord = await signupFormsApi.get(formId);
    let rules: EnrichmentData['rules'] = [];
    if (formRecord?.Builder_Schema) {
      try {
        const schema = JSON.parse(formRecord.Builder_Schema);
        console.log('[fetchEnrichmentData] Builder_Schema parsed:', {
          hasEmailRoutingRules: !!schema.emailRoutingRules,
          rulesCount: schema.emailRoutingRules?.length || 0,
          schemaKeys: Object.keys(schema),
          schemaLength: formRecord.Builder_Schema.length
        });
        if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
          // Fetch templates for each rule
          const resolvedRules = [];
          for (const rule of schema.emailRoutingRules) {
            console.log('[fetchEnrichmentData] Resolving rule:', {
              field: rule.field,
              operator: rule.operator,
              value: rule.value,
              templateId: rule.templateId
            });
            if (rule.templateId) {
              const ruleTemplate = await emailTemplatesApi.get(rule.templateId);
              if (ruleTemplate) {
                // Extract redirect URL from this template
                const rawLink = (ruleTemplate as any).Confirmation_Link;
                const ruleRedirectUrl = rawLink ? typeof rawLink === 'string' ? rawLink : rawLink?.url || '' : '';
                resolvedRules.push({
                  field: rule.field,
                  operator: rule.operator,
                  value: rule.value,
                  html: ruleTemplate.Email_HTML_Body || '',
                  subject: ruleTemplate.Email_Subject || '',
                  redirect: ruleRedirectUrl
                });
                console.log('[fetchEnrichmentData] ✅ Rule resolved:', {
                  field: rule.field,
                  value: rule.value,
                  templateSubject: ruleTemplate.Email_Subject,
                  htmlLength: (ruleTemplate.Email_HTML_Body || '').length,
                  redirectUrl: ruleRedirectUrl
                });
              } else {
                console.warn('[fetchEnrichmentData] ❌ Template not found for rule:', rule.templateId);
              }
            } else {
              console.warn('[fetchEnrichmentData] ❌ Rule has no templateId:', rule);
            }
          }
          rules = resolvedRules;
          console.log('[fetchEnrichmentData] Total resolved rules:', rules.length);
        }
      } catch (e) {
        console.error('[fetchEnrichmentData] Failed to parse Builder_Schema:', e);
      }
    } else {
      console.log('[fetchEnrichmentData] No Builder_Schema on form record');
    }
    // If no default template and no rules, return null
    if (!template && rules.length === 0) {
      console.log('[fetchEnrichmentData] No template and no rules — returning null');
      return null;
    }
    // 4. Find Newsletter linked to this form
    let newsletterId = '';
    try {
      const allNewsletters = await newslettersApi.list();
      const newsletter = allNewsletters.find(nl => {
        const nlFormId = extractLookupId(nl.Sign_Up_Form);
        return nlFormId === formId;
      });
      if (newsletter) {
        newsletterId = newsletter.Newsletter_ID || '';
      }
    } catch {
      // Newsletter lookup is optional — don't block
    }
    // 5. Extract confirmation/redirect URL from default template (if exists)
    let redirectUrl = '';
    if (template) {
      const rawLink = (template as any).Confirmation_Link;
      if (rawLink) {
        if (typeof rawLink === 'string') {
          redirectUrl = rawLink;
        } else if (typeof rawLink === 'object' && rawLink.url) {
          redirectUrl = rawLink.url;
        }
      }
    }
    // 6. Read confirmationLanguage from Builder_Schema (set by admin in Form Configuration)
    let confirmationLanguage = '';
    if (formRecord?.Builder_Schema) {
      try {
        const schema = JSON.parse(formRecord.Builder_Schema);
        if (schema.confirmationLanguage) {
          confirmationLanguage = schema.confirmationLanguage;
        }
      } catch {
        /* already parsed above, ignore */
      }
    }
    const result = {
      emailHtml: template?.Email_HTML_Body || '',
      emailSubject: template?.Email_Subject || '',
      newsletterId,
      region: config.Region || template?.Region || '',
      redirectUrl,
      appOrigin: window.location.origin,
      rules: rules.length > 0 ? rules : undefined,
      confirmationLanguage: confirmationLanguage || undefined
    };
    console.log('[fetchEnrichmentData] ✅ SUCCESS — returning enrichment:', {
      hasEmailHtml: !!result.emailHtml,
      emailHtmlLength: result.emailHtml.length,
      emailSubject: result.emailSubject,
      newsletterId: result.newsletterId,
      rulesCount: result.rules?.length || 0,
      rules: result.rules?.map(r => ({
        field: r.field,
        operator: r.operator,
        value: r.value,
        subjectLength: r.subject?.length,
        htmlLength: r.html?.length
      })),
      confirmationLanguage: result.confirmationLanguage
    });
    return result;
  } catch (err) {
    console.error('[fetchEnrichmentData] ❌ EXCEPTION — returning null:', err);
    return null;
  }
}
const DEFAULT_SUCCESS_MESSAGE = 'Successfully submitted! Please check your email to confirm.';
const DEFAULT_SUBMITTING_TEXT = 'Enviando…';
const DEFAULT_ERROR_MESSAGE = 'Error al enviar. Inténtalo de nuevo.';
const DEFAULT_VALIDATION_ERROR = 'Por favor, complete todos los campos obligatorios.';
/** Time-per-field heuristic (seconds) — mirrors Typeform's approach */
const FIELD_TIME_SECONDS: Record<string, number> = {
  text: 12,
  email: 12,
  phone: 12,
  url: 12,
  number: 8,
  textarea: 30,
  select: 6,
  radio: 5,
  checkbox: 8,
  consent: 5,
  date: 10,
  file: 20,
  hidden: 0
};
/** Layout block types that don't require user input */
const LAYOUT_BLOCK_TYPES = new Set(['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit']);
/** Calculate estimated completion time from builder blocks */
function calculateEstimatedTimeFromBlocks(blocks: FormBlock[]): number {
  let totalSeconds = 0;
  for (const block of blocks) {
    if (LAYOUT_BLOCK_TYPES.has(block.type)) continue;
    const seconds = FIELD_TIME_SECONDS[block.type] ?? 10;
    // Required fields take slightly longer (people are more careful)
    totalSeconds += block.required ? seconds + 3 : seconds;
    // Multi-option fields: add ~2s per extra option beyond 2
    if ((block.type === 'select' || block.type === 'radio' || block.type === 'checkbox') && block.validation?.options) {
      const extraOptions = Math.max(0, block.validation.options.length - 2);
      totalSeconds += extraOptions * 2;
    }
  }
  return totalSeconds;
}
/** Calculate estimated completion time from raw HTML (code mode fallback) */
function calculateEstimatedTimeFromHtml(html: string): number {
  if (!html) return 0;
  let totalSeconds = 0;
  // Count input types
  const inputRegex = /<input[^>]*type=["']?(text|email|tel|phone|url|number|date|file|checkbox|radio|hidden)["']?[^>]*>/gi;
  let match;
  while ((match = inputRegex.exec(html)) !== null) {
    const type = match[1].toLowerCase();
    const mappedType = type === 'tel' ? 'phone' : type === 'hidden' ? 'hidden' : type;
    totalSeconds += FIELD_TIME_SECONDS[mappedType] ?? 10;
  }
  // Count textareas
  const textareaCount = (html.match(/<textarea/gi) || []).length;
  totalSeconds += textareaCount * (FIELD_TIME_SECONDS.textarea ?? 30);
  // Count selects
  const selectCount = (html.match(/<select/gi) || []).length;
  totalSeconds += selectCount * (FIELD_TIME_SECONDS.select ?? 6);
  // Count inputs without explicit type (default = text)
  const inputNoType = (html.match(/<input(?![^>]*type=)[^>]*>/gi) || []).length;
  totalSeconds += inputNoType * (FIELD_TIME_SECONDS.text ?? 12);
  return totalSeconds;
}
/** Format seconds into a human-readable time string */
function formatEstimatedTime(totalSeconds: number): string {
  if (totalSeconds <= 0) return '';
  if (totalSeconds < 60) return '< 1 minute';
  const minutes = Math.round(totalSeconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}
export interface WelcomeScreenConfig {
  enabled: boolean;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  estimatedTime: string;
  backgroundColor: string;
  textColor: string;
  editorMode?: 'settings' | 'code' | 'builder';
  customHtml?: string;
  customCss?: string;
}
/**
 * Generate the self-contained submission handler script.
 */
export function generateSubmissionScript(team: string, slug: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string, postSubmitRedirectUrl?: string, logicRules?: FormLogicRule[]): string {
  console.log('[generateSubmissionScript][SignupFormEditor]', {
    team,
    slug,
    enrichment: enrichment ? {
      emailHtml: enrichment.emailHtml?.substring(0, 80),
      emailSubject: enrichment.emailSubject,
      newsletterId: enrichment.newsletterId,
      rulesCount: enrichment.rules?.length
    } : null,
    hasEnrichment: !!enrichment,
    hasEmailHtml: !!enrichment?.emailHtml,
    hasRules: !!enrichment?.rules?.length,
    caller: new Error().stack?.split('\n')[2]?.trim()
  });
  var safeTeam = team.replace(/'/g, "\\'").replace(/\\/g, '\\\\');
  var safeSlug = slug.replace(/'/g, "\\'").replace(/\\/g, '\\\\');
  var safeSuccessMsg = escapeForJSString(successMessage || DEFAULT_SUCCESS_MESSAGE);
  var safeSubmittingText = escapeForJSString(submittingText || DEFAULT_SUBMITTING_TEXT);
  var safeErrorMsg = escapeForJSString(errorMessage || DEFAULT_ERROR_MESSAGE);
  var safeValidationMsg = escapeForJSString(validationErrorMessage || DEFAULT_VALIDATION_ERROR);
  var safePostRedirect = postSubmitRedirectUrl ? escapeForJSString(postSubmitRedirectUrl) : '';
  // Pre-escape enrichment data for embedding in JS strings
  var safeEmailHtml = enrichment ? escapeForJSString(enrichment.emailHtml) : '';
  var safeEmailSubject = enrichment ? escapeForJSString(enrichment.emailSubject) : '';
  var safeNewsletterId = enrichment ? escapeForJSString(enrichment.newsletterId) : '';
  var safeRegion = enrichment ? escapeForJSString(enrichment.region) : '';
  var safeRedirectUrl = enrichment ? escapeForJSString(enrichment.redirectUrl) : '';
  var safeAppOrigin = enrichment ? escapeForJSString(enrichment.appOrigin) : '';
  var lines: string[] = [];
  lines.push('(function(){');
  lines.push("  var form=document.querySelector('form');");
  lines.push('  if(!form)return;');
  // Toast notification function — top center
  lines.push('  function showToast(msg,isErr){');
  lines.push("    var t=document.getElementById('_ft52');if(t)t.remove();");
  lines.push("    var s=document.createElement('style');");
  lines.push("    s.textContent='@keyframes _fti{from{transform:translate(-50%,-16px);opacity:0}to{transform:translate(-50%,0);opacity:1}}@keyframes _fto{from{opacity:1}to{opacity:0;transform:translate(-50%,-16px)}}';");
  lines.push('    document.head.appendChild(s);');
  lines.push("    t=document.createElement('div');t.id='_ft52';");
  lines.push("    t.style.cssText='position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:2147483647;background:'+(isErr?'#dc2626':'#16a34a')+';color:#fff;padding:14px 18px;border-radius:10px;font-size:14px;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:380px;display:flex;align-items:center;gap:12px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;animation:_fti 0.3s ease';");
  lines.push("    var sp=document.createElement('span');sp.textContent=msg;");
  lines.push("    var cl=document.createElement('button');cl.innerHTML='&times;';");
  lines.push("    cl.style.cssText='background:none;border:none;color:rgba(255,255,255,0.8);font-size:20px;cursor:pointer;padding:0;line-height:1;margin-left:4px;flex-shrink:0';");
  lines.push("    function dismiss(){t.style.animation='_fto 0.3s ease forwards';setTimeout(function(){if(t&&t.parentNode)t.remove()},300)}");
  lines.push('    cl.onclick=dismiss;t.appendChild(sp);t.appendChild(cl);document.body.appendChild(t);');
  lines.push('    setTimeout(dismiss,5000);');
  lines.push('  }');
  // Helper: generate a random confirmation token
  lines.push('  function genToken(){');
  lines.push('    var ts=Date.now().toString(36);');
  lines.push("    var r='';for(var i=0;i<16;i++)r+=Math.floor(Math.random()*36).toString(36);");
  lines.push("    return ts+'_'+r;");
  lines.push('  }');
  // Helper: replace {{variable}} placeholders in a template string
  lines.push('  function renderTpl(tpl,vars){');
  lines.push('    var result=tpl;');
  lines.push('    for(var k in vars){');
  lines.push('      if(!vars.hasOwnProperty(k))continue;');
  lines.push("      var re=new RegExp('\\\\{\\\\{\\\\s*'+k.replace(/[.*+?^${}()|[\\]\\\\]/g,'\\\\$&')+'\\\\s*\\\\}\\\\}','gi');");
  lines.push("      result=result.replace(re,vars[k]||'');");
  lines.push('    }');
  lines.push('    return result;');
  lines.push('  }');
  // Validation helper — checks visibility generically
  lines.push('  function isElVisible(el){');
  lines.push('    // Walk up ancestors to check for hidden elements');
  lines.push('    var node=el;');
  lines.push('    while(node&&node!==document.body){');
  lines.push('      // Check .hidden CSS class (common pattern)');
  lines.push("      if(node.classList&&node.classList.contains('hidden'))return false;");
  lines.push('      // Check inline display:none');
  lines.push("      if(node.style&&node.style.display==='none')return false;");
  lines.push('      // Check computed style for display:none or visibility:hidden');
  lines.push('      var cs=window.getComputedStyle(node);');
  lines.push("      if(cs.display==='none'||cs.visibility==='hidden')return false;");
  lines.push('      node=node.parentElement;');
  lines.push('    }');
  lines.push('    return true;');
  lines.push('  }');
  // Auto-mark required fields based on .req asterisks — visibility-aware
  // This ensures code-mode forms using .req labels get proper validation
  lines.push('  function autoMarkRequired(){');
  lines.push("    var labels=form.querySelectorAll('label.field-label, label[for]');");
  lines.push('    for(var i=0;i<labels.length;i++){');
  lines.push('      var lbl=labels[i];');
  lines.push("      if(!lbl.querySelector('.req'))continue;");
  lines.push('      var vis=isElVisible(lbl);');
  lines.push("      var forAttr=lbl.getAttribute('for');");
  lines.push('      if(forAttr){');
  lines.push("        var field=form.querySelector('#'+forAttr);");
  lines.push('        if(field){');
  lines.push("          if(vis)field.setAttribute('required','');");
  lines.push("          else field.removeAttribute('required');");
  lines.push('        }');
  lines.push('        continue;');
  lines.push('      }');
  lines.push('      var next=lbl.nextElementSibling;');
  lines.push('      if(next){');
  lines.push('        var radios=next.querySelectorAll(\'input[type="radio"],input[type="checkbox"]\');');
  lines.push('        for(var j=0;j<radios.length;j++){');
  lines.push("          if(vis)radios[j].setAttribute('required','');");
  lines.push("          else radios[j].removeAttribute('required');");
  lines.push('        }');
  lines.push('      }');
  lines.push("      var group=lbl.closest('.form-group');");
  lines.push('      if(group){');
  lines.push('        var cb=group.querySelector(\'input[type="checkbox"]\');');
  lines.push('        if(cb){');
  lines.push("          if(vis)cb.setAttribute('required','');");
  lines.push("          else cb.removeAttribute('required');");
  lines.push('        }');
  lines.push('      }');
  lines.push('    }');
  lines.push('  }');
  // Initialize autoMarkRequired and re-run on form changes
  lines.push('  autoMarkRequired();');
  lines.push("  form.addEventListener('change',function(){setTimeout(autoMarkRequired,30);});");
  lines.push('  if(window.MutationObserver){');
  lines.push("    var _secs=form.querySelectorAll('.persona-section,[data-persona],section');");
  lines.push('    if(_secs.length>0){');
  lines.push('      var _obs=new MutationObserver(function(){setTimeout(autoMarkRequired,30);});');
  lines.push("      for(var _s=0;_s<_secs.length;_s++){_obs.observe(_secs[_s],{attributes:true,attributeFilter:['class','style']});}");
  lines.push('    }');
  lines.push('  }');
  lines.push('  function validateForm(){');
  lines.push('    // Only validate visible required fields');
  lines.push("    var reqFields=form.querySelectorAll('[required]');");
  lines.push('    var checked={};');
  lines.push('    for(var i=0;i<reqFields.length;i++){');
  lines.push('      var el=reqFields[i];');
  lines.push('      // Skip if element or any ancestor is hidden');
  lines.push('      if(!isElVisible(el))continue;');
  lines.push("      if(el.type==='checkbox'){if(!el.checked)return false;}");
  lines.push("      else if(el.type==='radio'){");
  lines.push('        var name=el.name;');
  lines.push('        if(checked[name])continue;');
  lines.push('        checked[name]=true;');
  lines.push("        var group=form.querySelectorAll('input[name=\"'+name+'\"]');");
  lines.push('        var anyChecked=false;');
  lines.push('        for(var j=0;j<group.length;j++){if(group[j].checked){anyChecked=true;break;}}');
  lines.push('        if(!anyChecked)return false;');
  lines.push('      }');
  lines.push('      else{if(!el.value||!String(el.value).trim())return false;}');
  lines.push('    }');
  lines.push('    return true;');
  lines.push('  }');
  // --- Runtime Logic Engine ---
  if (logicRules && logicRules.length > 0) {
    const rulesJson = JSON.stringify(logicRules).replace(/<\/script>/gi, '<\\/script>');
    lines.push('  var logicRules=' + rulesJson + ';');
    lines.push('  var initialStates={};');
    lines.push('  var initialSelectOptions={};');
    lines.push('  function initLogic(){');
    lines.push('    var blocks=form.querySelectorAll("[data-block-id]");');
    lines.push('    for(var i=0;i<blocks.length;i++){');
    lines.push('      var b=blocks[i];');
    lines.push('      var id=b.getAttribute("data-block-id");');
    lines.push('      var reqs=[];');
    lines.push('      var inputs=b.querySelectorAll("input,select,textarea");');
    lines.push('      for(var j=0;j<inputs.length;j++){');
    lines.push('        if(inputs[j].hasAttribute("required")) reqs.push(inputs[j]);');
    lines.push('      }');
    lines.push('      initialStates[id]={ display: b.style.display, reqs: reqs };');
    lines.push('      var sel=b.querySelector("select");');
    lines.push('      if(sel){');
    lines.push('        var opts=[];');
    lines.push('        for(var s=0;s<sel.options.length;s++) opts.push(sel.options[s].cloneNode(true));');
    lines.push('        initialSelectOptions[id]=opts;');
    lines.push('      }');
    lines.push('    }');
    lines.push('  }');
    lines.push('  function evaluateLogic(){');
    lines.push('    // Reset all to initial');
    lines.push('    for(var id in initialStates){');
    lines.push('      var b=form.querySelector("[data-block-id=\'"+id+"\']");');
    lines.push('      if(!b)continue;');
    lines.push('      b.style.display=initialStates[id].display;');
    lines.push('      var inputs=b.querySelectorAll("input,select,textarea");');
    lines.push('      for(var j=0;j<inputs.length;j++) inputs[j].removeAttribute("required");');
    lines.push('      for(var k=0;k<initialStates[id].reqs.length;k++) initialStates[id].reqs[k].setAttribute("required","");');
    lines.push('      // Reset select options to original');
    lines.push('      if(initialSelectOptions[id]){');
    lines.push('        var sel=b.querySelector("select");');
    lines.push('        if(sel){');
    lines.push('          var curVal=sel.value;');
    lines.push('          sel.innerHTML="";');
    lines.push('          for(var s=0;s<initialSelectOptions[id].length;s++) sel.appendChild(initialSelectOptions[id][s].cloneNode(true));');
    lines.push('          sel.value=curVal;');
    lines.push('        }');
    lines.push('      }');
    lines.push('    }');
    lines.push('    // Evaluate rules');
    lines.push('    for(var i=0;i<logicRules.length;i++){');
    lines.push('      var rule=logicRules[i];');
    lines.push('      var matchCount=0;');
    lines.push('      for(var j=0;j<rule.conditions.length;j++){');
    lines.push('        var cond=rule.conditions[j];');
    lines.push('        var triggerBlock=form.querySelector("[data-block-id=\'"+cond.fieldId+"\']");');
    lines.push('        if(!triggerBlock)continue;');
    lines.push('        var input=triggerBlock.querySelector("input,select,textarea");');
    lines.push('        if(!input)continue;');
    lines.push('        var val="";');
    lines.push('        if(input.type==="checkbox"||input.type==="radio"){');
    lines.push('          var checked=triggerBlock.querySelector("input:checked");');
    lines.push('          val=checked?checked.value:"";');
    lines.push('        }else{ val=input.value; }');
    lines.push('        val=(val||"").trim().toLowerCase();');
    lines.push('        var targetVal=(cond.value||"").trim().toLowerCase();');
    lines.push('        var m=false;');
    lines.push('        switch(cond.operator){');
    lines.push('          case "equals": m=(val===targetVal); break;');
    lines.push('          case "not_equals": m=(val!==targetVal); break;');
    lines.push('          case "contains": m=(val.indexOf(targetVal)!==-1); break;');
    lines.push('          case "not_contains": m=(val.indexOf(targetVal)===-1); break;');
    lines.push('          case "is_empty": m=(val===""); break;');
    lines.push('          case "is_not_empty": m=(val!==""); break;');
    lines.push('          case "greater_than": m=(parseFloat(val)>parseFloat(targetVal)); break;');
    lines.push('          case "less_than": m=(parseFloat(val)<parseFloat(targetVal)); break;');
    lines.push('          case "one_of": m=(targetVal.split(",").map(function(s){return s.trim()}).indexOf(val)!==-1); break;');
    lines.push('        }');
    lines.push('        if(m) matchCount++;');
    lines.push('      }');
    lines.push('      var isMatch = rule.conditionMatch==="any" ? matchCount>0 : matchCount===rule.conditions.length;');
    lines.push('      if(isMatch){');
    lines.push('        for(var k=0;k<rule.actions.length;k++){');
    lines.push('          var act=rule.actions[k];');
    lines.push('          var target=form.querySelector("[data-block-id=\'"+act.targetId+"\']");');
    lines.push('          if(!target)continue;');
    lines.push('          if(act.type==="show_block") target.style.display="block";');
    lines.push('          else if(act.type==="hide_block") target.style.display="none";');
    lines.push('          else if(act.type==="require_block"){');
    lines.push('            var ins=target.querySelectorAll("input,select,textarea");');
    lines.push('            for(var l=0;l<ins.length;l++) ins[l].setAttribute("required","");');
    lines.push('          }');
    lines.push('          else if(act.type==="optional_block"){');
    lines.push('            var ins=target.querySelectorAll("input,select,textarea");');
    lines.push('            for(var l=0;l<ins.length;l++) ins[l].removeAttribute("required");');
    lines.push('          }');
    lines.push('          else if(act.type==="set_field_value"){');
    lines.push('            var inp=target.querySelector("input,select,textarea");');
    lines.push('            if(inp) inp.value=act.value||"";');
    lines.push('          }');
    lines.push('          else if(act.type==="jump_to_block"){');
    lines.push('            var inp=target.querySelector("input,select,textarea");');
    lines.push('            if(inp) inp.focus();');
    lines.push('          }');
    lines.push('          else if(act.type==="filter_options"&&act.filterValues){');
    lines.push('            var sel=target.querySelector("select");');
    lines.push('            if(sel){');
    lines.push('              var tid=target.getAttribute("data-block-id");');
    lines.push('              var origOpts=initialSelectOptions[tid];');
    lines.push('              if(origOpts){');
    lines.push('                var curVal=sel.value;');
    lines.push('                sel.innerHTML="";');
    lines.push('                var fv=act.filterValues;');
    lines.push('                for(var f=0;f<origOpts.length;f++){');
    lines.push('                  var ov=origOpts[f].value;');
    lines.push('                  if(!ov||fv.indexOf(ov)!==-1) sel.appendChild(origOpts[f].cloneNode(true));');
    lines.push('                }');
    lines.push('                if(fv.indexOf(curVal)!==-1) sel.value=curVal; else sel.selectedIndex=0;');
    lines.push('              }');
    lines.push('            }');
    lines.push('          }');
    lines.push('        }');
    lines.push('      }');
    lines.push('    }');
    lines.push('  }');
    lines.push('  initLogic();');
    lines.push('  form.addEventListener("change", evaluateLogic);');
    lines.push('  form.addEventListener("input", evaluateLogic);');
    lines.push('  evaluateLogic();');
  }
  // --- End Runtime Logic Engine ---
  // Intercept submit
  lines.push("  form.addEventListener('submit',function(e){");
  lines.push('    e.preventDefault();');
  lines.push('    e.stopImmediatePropagation();');
  // Validate required fields
  lines.push("    if(!validateForm()){showToast('" + safeValidationMsg + "',true);return;}");
  // Collect all form fields
  lines.push('    var fd=new FormData(form);');
  lines.push('    var fields={};');
  lines.push('    fd.forEach(function(v,k){fields[k]=v;});');
  // Fallback: manually collect from inputs
  lines.push('    if(Object.keys(fields).length===0){');
  lines.push("      var inputs=form.querySelectorAll('input,select,textarea');");
  lines.push('      for(var i=0;i<inputs.length;i++){');
  lines.push('        var el=inputs[i];');
  lines.push("        var n=el.getAttribute('name')||el.getAttribute('id')||'';");
  lines.push("        if(n&&el.type!=='submit'&&el.type!=='button'){");
  lines.push("          if(el.type==='checkbox')fields[n]=el.checked?'yes':'no';");
  lines.push("          else if(el.type==='radio'){if(el.checked)fields[n]=el.value;}");
  lines.push("          else fields[n]=el.value||el.textContent||'';");
  lines.push('        }');
  lines.push('      }');
  lines.push('    }');
  // Disable button with configurable text
  lines.push('    var btn=form.querySelector(\'[type="submit"],button:not([type])\');');
  lines.push('    var origText="";');
  lines.push('    if(btn){origText=btn.textContent;btn.disabled=true;btn.textContent="' + safeSubmittingText + '";}');
  // Determine subscriber email
  lines.push("    var email=fields.email||fields.Email||fields.email_address||fields.EMAIL||fields.emailAddress||fields.e_mail||'';");
  // Generate confirmation token
  lines.push('    var token=genToken();');
  // Build confirmation URL (with optional language param set by admin)
  var safeLang = enrichment?.confirmationLanguage ? escapeForJSString(enrichment.confirmationLanguage) : '';
  var langSuffix = safeLang ? "+'&lang=" + safeLang + "'" : '';
  if (enrichment && enrichment.appOrigin) {
    lines.push("    var confirmUrl='" + safeAppOrigin + "/confirm?token='+encodeURIComponent(token)" + langSuffix + ';');
  } else {
    lines.push("    var confirmUrl=window.location.origin+'/confirm?token='+encodeURIComponent(token)" + langSuffix + ';');
  }
  // Render email template if enrichment data is available
  if (enrichment && (enrichment.emailHtml || enrichment.rules && enrichment.rules.length > 0)) {
    lines.push('    var tplVars={}');
    lines.push('    for(var fk in fields){if(fields.hasOwnProperty(fk))tplVars[fk]=fields[fk];}');
    lines.push('    tplVars.confirmation_link=confirmUrl;');
    lines.push("    tplVars.team_name='" + safeTeam + "';");
    lines.push("    var tplHtml='" + safeEmailHtml + "';");
    lines.push("    var tplSubject='" + safeEmailSubject + "';");
    lines.push("    var matchedRedirectUrl='" + safeRedirectUrl + "';");
    // Inject conditional rules
    if (enrichment.rules && enrichment.rules.length > 0) {
      lines.push('    var rules=' + JSON.stringify(enrichment.rules).replace(/<\/script>/gi, '<\\/script>') + ';');
      lines.push('    console.log("[SubmissionScript] Evaluating "+rules.length+" routing rules against fields:",JSON.stringify(fields));');
      lines.push('    for(var ri=0; ri<rules.length; ri++){');
      lines.push('      var r=rules[ri];');
      lines.push("      var fVal=(fields[r.field]||'').trim().toLowerCase();");
      lines.push("      var rVal=(r.value||'').trim().toLowerCase();");
      lines.push('      var match=false;');
      lines.push("      if(r.operator==='equals'&&fVal===rVal)match=true;");
      lines.push("      else if(r.operator==='not_equals'&&fVal!==rVal)match=true;");
      lines.push("      else if(r.operator==='contains'&&fVal.indexOf(rVal)!==-1)match=true;");
      lines.push('      console.log("[SubmissionScript] Rule "+ri+": field="+r.field+" fVal=\\""+fVal+"\\" op="+r.operator+" rVal=\\""+rVal+"\\" → match="+match);');
      lines.push('      if(match){');
      lines.push('        tplHtml=r.html;');
      lines.push('        tplSubject=r.subject;');
      lines.push('        if(r.redirect)matchedRedirectUrl=r.redirect;');
      lines.push('        console.log("[SubmissionScript] ✅ Rule "+ri+" MATCHED — using subject: "+r.subject+" redirect: "+matchedRedirectUrl);');
      lines.push('        break;');
      lines.push('      }');
      lines.push('    }');
    } else {
      lines.push('    console.log("[SubmissionScript] No routing rules in enrichment — using default template");');
    }
    lines.push('    var renderedHtml=tplHtml ? renderTpl(tplHtml,tplVars) : "";');
    lines.push('    var renderedSubject=tplSubject ? renderTpl(tplSubject,tplVars) : "";');
    lines.push('    console.log("[SubmissionScript] Final rendered — subject:",renderedSubject.substring(0,80),"htmlLength:",renderedHtml.length);');
  } else {
    lines.push('    console.log("[SubmissionScript] ⚠️ NO ENRICHMENT — renderedHtml and renderedSubject will be EMPTY. Enrichment was null/missing when script was generated.");');
    lines.push("    var renderedHtml='';");
    lines.push("    var renderedSubject='';");
  }
  // Detect consent checkbox state at submission time
  // Works for both builder (name="consent") and code-method forms (any checkbox with name containing "consent")
  lines.push('    var consentChecked=false;');
  lines.push('    var consentInputs=form.querySelectorAll(\'input[type="checkbox"]\');');
  lines.push('    for(var ci=0;ci<consentInputs.length;ci++){');
  lines.push("      var cn=(consentInputs[ci].name||'').toLowerCase();");
  lines.push("      if(cn.indexOf('consent')!==-1 && consentInputs[ci].checked){consentChecked=true;break;}");
  lines.push('    }');
  // Determine status: if no opt-in email is configured but consent is checked, auto-confirm
  const hasOptInEmail = !!(enrichment && (enrichment.emailHtml || enrichment.rules && enrichment.rules.length > 0));
  if (hasOptInEmail) {
    // Normal double opt-in flow — always start as Not Subscribed
    lines.push("    var submitStatus='Not Subscribed';");
  } else {
    // No opt-in email configured — auto-confirm regardless of consent field presence
    lines.push("    var submitStatus='Confirmed';");
  }
  // Build the full submission payload
  lines.push('    var payload={');
  lines.push('      Form_Payload:JSON.stringify(fields),');
  lines.push('      Status:submitStatus,');
  lines.push("      Team:'" + safeTeam + "',");
  lines.push('      Subscriber_Email:email,');
  lines.push('      Confirmation_Token:token,');
  if (enrichment && enrichment.newsletterId) {
    lines.push("      Newsletter_ID:'" + safeNewsletterId + "',");
  }
  if (enrichment && enrichment.region) {
    lines.push("      Region:'" + safeRegion + "',");
  }
  lines.push('      Redirect_URL:matchedRedirectUrl,');
  lines.push('      Rendered_Email_HTML:renderedHtml,');
  lines.push('      Rendered_Email_Subject:renderedSubject,');
  lines.push('      Added_Time:new Date().toISOString()');
  lines.push('    };');
  lines.push('    console.log("[SubmissionScript] 📤 INITIAL POST payload:",{subject:payload.Rendered_Email_Subject,htmlLength:payload.Rendered_Email_HTML?.length||0,status:payload.Status,email:payload.Subscriber_Email});');
  // POST to the API proxy
  lines.push("    fetch('https://api.thechosen.tv/chosen_zoho_api.php',{");
  lines.push("      method:'POST',");
  lines.push("      headers:{'Content-Type':'application/json','API-KEY':'ud7^3jf(80'},");
  lines.push('      body:JSON.stringify({');
  lines.push("        url:'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/form/Submissions',");
  lines.push("        method:'POST',");
  lines.push('        data:{data:payload}');
  lines.push('      })');
  lines.push('    })');
  lines.push('    .then(function(r){return r.json();})');
  lines.push('    .then(function(res){');
  // After successful submission, extract record ID and update the confirmation URL
  // This ensures the confirmation link in the email has both token AND record ID
  lines.push("      var recordId=res&&res.data&&res.data.ID?String(res.data.ID):'';");
  lines.push('      if(recordId){');
  // Rebuild confirmation URL with record ID (and language param if set)
  if (enrichment && enrichment.appOrigin) {
    lines.push("        var fullConfirmUrl='" + safeAppOrigin + "/confirm?token='+encodeURIComponent(token)+'&id='+encodeURIComponent(recordId)" + langSuffix + ';');
  } else {
    lines.push("        var fullConfirmUrl=window.location.origin+'/confirm?token='+encodeURIComponent(token)+'&id='+encodeURIComponent(recordId)" + langSuffix + ';');
  }
  // Re-render email template with the updated confirmation URL that includes record ID
  if (enrichment && (enrichment.emailHtml || enrichment.rules && enrichment.rules.length > 0)) {
    lines.push('        tplVars.confirmation_link=fullConfirmUrl;');
    lines.push('        var updatedHtml=tplHtml ? renderTpl(tplHtml,tplVars) : "";');
    lines.push('        var updatedSubject=tplSubject ? renderTpl(tplSubject,tplVars) : "";');
  } else {
    lines.push("        var updatedHtml='';");
    lines.push("        var updatedSubject='';");
  }
  // PATCH the submission record with the corrected rendered email HTML
  lines.push('        console.log("[SubmissionScript] 📤 PATCH payload:",{recordId:recordId,subject:updatedSubject,htmlLength:updatedHtml?.length||0});');
  lines.push("        fetch('https://api.thechosen.tv/chosen_zoho_api.php',{");
  lines.push("          method:'POST',");
  lines.push("          headers:{'Content-Type':'application/json','API-KEY':'ud7^3jf(80'},");
  lines.push('          body:JSON.stringify({');
  lines.push("            url:'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/report/All_Submissions/'+recordId,");
  lines.push("            method:'PATCH',");
  lines.push('            data:{data:{Rendered_Email_HTML:updatedHtml,Rendered_Email_Subject:updatedSubject}}');
  lines.push('          })');
  lines.push("        }).then(function(r){return r.json();}).then(function(patchRes){console.log('[SubmissionScript] ✅ PATCH response:',patchRes);}).catch(function(e){console.error('[SubmissionScript] ❌ PATCH failed:',e);});");
  lines.push('      }');
  if (postSubmitRedirectUrl) {
    lines.push("      window.location.href='" + safePostRedirect + "';");
  } else {
    lines.push("      showToast('" + safeSuccessMsg + "',false);");
    lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
    lines.push('      form.reset();');
  }
  lines.push('    })');
  lines.push('    .catch(function(err){');
  lines.push("      showToast('" + safeErrorMsg + "',true);");
  lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
  lines.push("      console.error('Form submission error:',err);");
  lines.push('    });');
  lines.push('  },true);');
  lines.push('})();');
  return lines.join('\n');
}
/** Generate the Welcome Screen HTML wrapper */
export function generateWelcomeScreenHtml(config: WelcomeScreenConfig, formHtml: string, containerId: string): string {
  if (!config.enabled) return formHtml;
  // Custom HTML mode (code or builder)
  if (config.editorMode === 'code' || config.editorMode === 'builder') {
    const customHtml = config.customHtml || '';
    const customCss = config.customCss || '';
    if (customHtml) {
      const welcomeId = `welcome-${containerId}`;
      const formWrapId = `formwrap-${containerId}`;
      let html = '';
      if (customCss) {
        html += '<sty' + 'le>' + customCss + '</sty' + 'le>';
      }
      html += `<div id="${welcomeId}" style="transition:opacity 0.4s ease;">`;
      html += customHtml;
      html += `</div>`;
      html += `<div id="${formWrapId}" style="display:none; opacity:0; transition:opacity 0.4s ease;">`;
      html += formHtml;
      html += `</div>`;
      html += `<script>(function(){var wb=document.getElementById('${welcomeId}');var fw=document.getElementById('${formWrapId}');if(!wb||!fw)return;var btns=wb.querySelectorAll('button,[data-action=start],.welcome-start-btn,[type=submit]');for(var i=0;i<btns.length;i++){btns[i].addEventListener('click',function(e){e.preventDefault();wb.style.opacity='0';setTimeout(function(){wb.style.display='none';fw.style.display='block';setTimeout(function(){fw.style.opacity='1';},50);},400);});}})();<\/script>`;
      return html;
    }
  }
  const bg = escapeForJSString(config.backgroundColor || '#000000');
  const text = escapeForJSString(config.textColor || '#ffffff');
  const title = escapeForJSString(config.title || '');
  const desc = escapeForJSString(config.description || '');
  const btn = escapeForJSString(config.buttonText || 'Get Started');
  const img = config.imageUrl ? escapeForJSString(config.imageUrl) : '';
  const time = config.estimatedTime ? escapeForJSString(config.estimatedTime) : '';
  const welcomeId = `welcome-${containerId}`;
  const formWrapId = `formwrap-${containerId}`;
  let html = `<div id="${welcomeId}" style="background-color:${bg}; color:${text}; min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:2rem; font-family:system-ui,-apple-system,sans-serif; text-align:center; transition:opacity 0.4s ease;">`;
  if (img) {
    html += `<img src="${img}" alt="Welcome" style="max-width:100%; height:auto; max-height:40vh; object-fit:cover; margin-bottom:2rem; border-radius:8px;" />`;
  }
  if (title) {
    html += `<h1 style="font-size:2.5rem; font-weight:700; margin-bottom:1rem; line-height:1.2;">${title}</h1>`;
  }
  if (desc) {
    html += `<p style="font-size:1.125rem; opacity:0.9; max-width:600px; margin-bottom:2.5rem; line-height:1.6; white-space:pre-wrap;">${desc}</p>`;
  }
  html += `<button onclick="document.getElementById('${welcomeId}').style.opacity='0'; setTimeout(function(){ document.getElementById('${welcomeId}').style.display='none'; document.getElementById('${formWrapId}').style.display='block'; setTimeout(function(){ document.getElementById('${formWrapId}').style.opacity='1'; }, 50); }, 400);" style="background-color:#eab308; color:#000; border:none; padding:1rem 3rem; font-size:1.125rem; font-weight:600; border-radius:8px; cursor:pointer; transition:transform 0.2s; box-shadow:0 4px 6px rgba(0,0,0,0.1);">`;
  html += `${btn}</button>`;
  if (time) {
    html += `<p style="margin-top:1rem; font-size:0.875rem; opacity:0.7; display:flex; align-items:center; gap:0.5rem;"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg> Takes ${time}</p>`;
  }
  html += `</div>`;
  html += `<div id="${formWrapId}" style="display:none; opacity:0; transition:opacity 0.4s ease; min-height:100vh; background-color:${bg}; padding:2rem;">`;
  html += formHtml;
  html += `</div>`;
  return html;
}
/** Generate a self-contained JS embed snippet */
function generateJsEmbed(slug: string, html: string, css: string, js: string, team: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string, postSubmitRedirectUrl?: string, welcomeConfig?: WelcomeScreenConfig | null, logicRules?: FormLogicRule[]): string {
  if (!html.trim()) return '';
  var containerId = 'form-52s-' + (slug || 'preview');
  var finalHtml = welcomeConfig?.enabled ? generateWelcomeScreenHtml(welcomeConfig, html, containerId) : html;
  var safeHtml = escapeForJSString(finalHtml);
  var safeCss = escapeForJSString(css);
  var safeJs = js.replace(/<\/script>/gi, '<\\/script>');
  var submissionScript = generateSubmissionScript(team, slug, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, logicRules);
  var safeSubmission = submissionScript.replace(/<\/script>/gi, '<\\/script>');
  var lines: string[] = [];
  lines.push('<div id="' + containerId + '"></div>');
  lines.push('<script>');
  lines.push('(function(){');
  lines.push("  var c=document.getElementById('" + containerId + "');");
  lines.push('  if(!c)return;');
  lines.push("  c.innerHTML='<sty" + 'le>' + safeCss + '</sty' + 'le>' + safeHtml + "';");
  lines.push('  var sh=document.createElement("script");');
  lines.push('  sh.textContent=' + JSON.stringify(safeSubmission) + ';');
  lines.push('  c.appendChild(sh);');
  if (safeJs.trim()) {
    lines.push('  var us=document.createElement("script");');
    lines.push('  us.textContent=' + JSON.stringify(safeJs) + ';');
    lines.push('  c.appendChild(us);');
  }
  lines.push('})();');
  lines.push('<\/script>');
  return lines.join('\n');
}
/** Generate a self-contained iframe embed using srcdoc */
function generateIframeEmbed(slug: string, html: string, css: string, js: string, team: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string, postSubmitRedirectUrl?: string, welcomeConfig?: WelcomeScreenConfig | null, logicRules?: FormLogicRule[]): string {
  if (!html.trim()) return '';
  var resetCss = '*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;padding:1rem}';
  var fullCss = resetCss + (css || '');
  var containerId = 'iframe-' + (slug || 'preview');
  var finalHtml = welcomeConfig?.enabled ? generateWelcomeScreenHtml(welcomeConfig, html, containerId) : html;
  var submissionScript = generateSubmissionScript(team, slug, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, logicRules);
  var docParts: string[] = [];
  docParts.push('<!DOCTYPE html><html><head><meta charset="utf-8">');
  docParts.push('<meta name="viewport" content="width=device-width,initial-scale=1">');
  docParts.push('<sty' + 'le>' + fullCss + '</sty' + 'le>');
  docParts.push('</head><body style="margin:0;padding:0;background:' + (welcomeConfig?.enabled ? welcomeConfig.backgroundColor : 'transparent') + '">');
  docParts.push(finalHtml);
  docParts.push('<script>' + submissionScript + '<\/script>');
  if (js && js.trim()) {
    docParts.push('<script>' + js + '<\/script>');
  }
  docParts.push('</body></html>');
  var doc = docParts.join('');
  var srcdoc = doc.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return '<iframe srcdoc="' + srcdoc + '" width="100%" height="100%" style="min-height:600px;border:none;overflow:hidden;" allowtransparency="true"></iframe>';
}
/** Extract form field names from HTML */
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
/** Check if form HTML changes affect linked email templates */
async function checkVariableImpact(formId: string, oldHtml: string, newHtml: string): Promise<{
  affected: boolean;
  removedFields: string[];
  templateSubjects: string[];
}> {
  const oldFields = extractFormFields(oldHtml);
  const newFields = extractFormFields(newHtml);
  const removedFields = oldFields.filter(f => !newFields.includes(f));
  if (removedFields.length === 0) {
    return {
      affected: false,
      removedFields: [],
      templateSubjects: []
    };
  }
  const configs = await formConfigsApi.findByFormId(formId);
  if (configs.length === 0) {
    return {
      affected: false,
      removedFields,
      templateSubjects: []
    };
  }
  const templateSubjects: string[] = [];
  for (const config of configs) {
    const templateId = extractLookupId(config.Email_Template);
    if (!templateId) continue;
    try {
      const template = await emailTemplatesApi.get(templateId);
      if (!template) continue;
      const html = template.Email_HTML_Body || '';
      const usesRemoved = removedFields.some(f => html.includes('{{' + f + '}}'));
      if (usesRemoved) {
        templateSubjects.push(template.Email_Subject || 'Template ' + templateId);
      }
    } catch {
      // Skip templates we can't fetch
    }
  }
  return {
    affected: templateSubjects.length > 0,
    removedFields,
    templateSubjects
  };
}
/**
 * Refresh embed codes for a given signup form.
 * Called from FormConfiguration after saving routing rules so the
 * deployed embed codes include the latest conditional email logic.
 */
export async function refreshFormEmbedCodes(formId: string): Promise<boolean> {
  try {
    // 1. Fetch the full form record
    const form = await signupFormsApi.get(formId);
    if (!form || !form.HTML) {
      console.log('[refreshFormEmbedCodes] Form not found or has no HTML:', formId);
      return false;
    }
    // 2. Parse Builder_Schema for settings
    let successMessage = 'Successfully submitted! Please check your email to confirm.';
    let submittingText = 'Enviando…';
    let errorMessage = 'Error al enviar. Inténtalo de nuevo.';
    let validationErrorMessage = 'Por favor, complete todos los campos obligatorios.';
    let postSubmitRedirectUrl = '';
    let welcomeConfig: WelcomeScreenConfig | null = null;
    let logicRules: FormLogicRule[] = [];
    if (form.Builder_Schema) {
      try {
        const schema = JSON.parse(form.Builder_Schema);
        if (schema.successMessage) successMessage = schema.successMessage;
        if (schema.submittingText) submittingText = schema.submittingText;
        if (schema.errorMessage) errorMessage = schema.errorMessage;
        if (schema.validationErrorMessage) validationErrorMessage = schema.validationErrorMessage;
        if (schema.postSubmitRedirectUrl) postSubmitRedirectUrl = schema.postSubmitRedirectUrl;
        if (schema.welcomeScreen) {
          welcomeConfig = schema.welcomeScreen;
        }
        if (schema.rules) {
          logicRules = schema.rules;
        }
      } catch {
        /* ignore parse errors */
      }
    }
    // 3. Fetch enrichment data (resolves routing rules into template HTML)
    const enrichment = await fetchEnrichmentData(formId);
    console.log('[refreshFormEmbedCodes] Enrichment resolved:', {
      hasEnrichment: !!enrichment,
      rulesCount: enrichment?.rules?.length || 0,
      emailSubject: enrichment?.emailSubject || '(none)'
    });
    // 4. Generate new embed codes
    const slug = form.Url_Slug || '';
    const team = form.Team || '';
    const html = form.HTML || '';
    const css = form.CSS || '';
    const js = form.JavaScript1 || '';
    const newJsEmbed = generateJsEmbed(slug, html, css, js, team, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, logicRules);
    const newIframeEmbed = generateIframeEmbed(slug, html, css, js, team, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, logicRules);
    // 5. Update the form record with new embed codes
    await signupFormsApi.update(formId, {
      JavaScript_Embed: newJsEmbed,
      Iframe_Embed: newIframeEmbed
    });
    console.log('[refreshFormEmbedCodes] ✅ Embed codes refreshed for form:', formId);
    return true;
  } catch (err) {
    console.error('[refreshFormEmbedCodes] Failed:', err);
    return false;
  }
}
export function SignupFormEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  const [showPreview, setShowPreview] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showBuilderConfirm, setShowBuilderConfirm] = useState(false);
  // Builder state
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  const [builderRules, setBuilderRules] = useState<FormLogicRule[]>([]);
  // FIX: Team is always the workspace name, Region is the sub-region
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const region = currentSubRegion || '';
  const [formName, setFormName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState('active');
  const [formData, setFormData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [originalHtml, setOriginalHtml] = useState('');
  const [enrichmentData, setEnrichmentData] = useState<EnrichmentData | null>(null);
  const [isLoadingEnrichment, setIsLoadingEnrichment] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS_MESSAGE);
  const [submittingText, setSubmittingText] = useState(DEFAULT_SUBMITTING_TEXT);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [validationErrorMessage, setValidationErrorMessage] = useState(DEFAULT_VALIDATION_ERROR);
  // New features state
  const [landingPages, setLandingPages] = useState<LandingPageRecord[]>([]);
  const [postSubmitRedirectUrl, setPostSubmitRedirectUrl] = useState('');
  const [welcomeScreenEnabled, setWelcomeScreenEnabled] = useState(false);
  const [welcomeTitle, setWelcomeTitle] = useState('');
  const [welcomeDescription, setWelcomeDescription] = useState('');
  const [welcomeButtonText, setWelcomeButtonText] = useState('Join Us');
  const [welcomeImageUrl, setWelcomeImageUrl] = useState('');
  const [welcomeEstimatedTime, setWelcomeEstimatedTime] = useState('');
  const [welcomeBgColor, setWelcomeBgColor] = useState('#000000');
  const [welcomeTextColor, setWelcomeTextColor] = useState('#ffffff');
  const [showWelcomeSettings, setShowWelcomeSettings] = useState(false);
  // Preserve email routing rules set via Form Configuration
  const [emailRoutingRules, setEmailRoutingRules] = useState<any[]>([]);
  // Preserve confirmation page language set via Form Configuration
  const [confirmationLanguage, setConfirmationLanguage] = useState('');
  // Welcome screen editor mode state
  const [welcomeEditorMode, setWelcomeEditorMode] = useState<'settings' | 'code' | 'builder'>('settings');
  const [welcomeCustomHtml, setWelcomeCustomHtml] = useState('');
  const [welcomeCustomCss, setWelcomeCustomCss] = useState('');
  const [welcomeActiveTab, setWelcomeActiveTab] = useState<'html' | 'css'>('html');
  const [welcomeBuilderBlocks, setWelcomeBuilderBlocks] = useState<FormBlock[]>([]);
  const [welcomeBuilderTheme, setWelcomeBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  // Auto-estimate toggle for welcome screen time
  const [welcomeAutoEstimate, setWelcomeAutoEstimate] = useState(true);
  // Template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templatePickerTemplates, setTemplatePickerTemplates] = useState<FormTemplateRecord[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateApplied, setTemplateApplied] = useState(false);
  // Add new state for full-screen welcome editor
  const [showWelcomeScreenEditor, setShowWelcomeScreenEditor] = useState(false);
  const [showFormSettings, setShowFormSettings] = useState(false);
  const [isExtractingFields, setIsExtractingFields] = useState(false);
  // Load landing pages for the redirect dropdown
  useEffect(() => {
    landingPagesApi.list().then(pages => {
      const teamFiltered = pages.filter(p => p.Team === currentWorkspace.name || p.Team === team);
      setLandingPages(teamFiltered);
    }).catch(err => console.error('Failed to load landing pages', err));
  }, [currentWorkspace.name, team]);
  // Auto-calculate estimated time when welcome screen is enabled and auto mode is on
  useEffect(() => {
    if (!welcomeScreenEnabled || !welcomeAutoEstimate) return;
    let totalSeconds = 0;
    if (editorMode === 'builder' && builderBlocks.length > 0) {
      totalSeconds = calculateEstimatedTimeFromBlocks(builderBlocks);
    } else if (formData.html) {
      totalSeconds = calculateEstimatedTimeFromHtml(formData.html);
    }
    const formatted = formatEstimatedTime(totalSeconds);
    setWelcomeEstimatedTime(formatted);
  }, [welcomeScreenEnabled, welcomeAutoEstimate, builderBlocks, formData.html, editorMode]);
  const handleCopyEmbed = (type: string, text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedEmbed(type);
      toast.success('Embed code copied to clipboard');
      setTimeout(() => setCopiedEmbed(null), 2000);
    }).catch(() => {
      // Fallback for non-HTTPS contexts
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setCopiedEmbed(type);
      toast.success('Embed code copied to clipboard');
      setTimeout(() => setCopiedEmbed(null), 2000);
    });
  };
  useEffect(() => {
    if (!slugManuallyEdited && !isEditing) {
      setUrlSlug(toSlug(formName));
    }
  }, [formName, slugManuallyEdited, isEditing]);
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      signupFormsApi.get(id).then(async record => {
        if (record) {
          setFormName(record.Form_Name || '');
          setUrlSlug(record.Url_Slug || '');
          setSlugManuallyEdited(true);
          setStatus(record.Status || 'active');
          setFormData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          setOriginalHtml(record.HTML || '');
          // Restore builder state and settings from Builder_Schema
          if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema & {
                successMessage?: string;
                submittingText?: string;
                errorMessage?: string;
                validationErrorMessage?: string;
                postSubmitRedirectUrl?: string;
                welcomeScreen?: WelcomeScreenConfig;
              } = JSON.parse(record.Builder_Schema);
              // Restore success message
              if (schema.successMessage) {
                setSuccessMessage(schema.successMessage);
              }
              if (schema.submittingText) {
                setSubmittingText(schema.submittingText);
              }
              if (schema.errorMessage) {
                setErrorMessage(schema.errorMessage);
              }
              if (schema.validationErrorMessage) {
                setValidationErrorMessage(schema.validationErrorMessage);
              }
              if (schema.postSubmitRedirectUrl) {
                setPostSubmitRedirectUrl(schema.postSubmitRedirectUrl);
              }
              if (schema.welcomeScreen) {
                setWelcomeScreenEnabled(schema.welcomeScreen.enabled || false);
                setWelcomeTitle(schema.welcomeScreen.title || '');
                setWelcomeDescription(schema.welcomeScreen.description || '');
                setWelcomeButtonText(schema.welcomeScreen.buttonText || 'Join Us');
                setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
                setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
                setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '#000000');
                setWelcomeTextColor(schema.welcomeScreen.textColor || '#ffffff');
                if (schema.welcomeScreen.enabled) setShowWelcomeSettings(true);
                // Restore welcome editor mode and custom HTML
                if (schema.welcomeScreen.editorMode) {
                  setWelcomeEditorMode(schema.welcomeScreen.editorMode);
                }
                if (schema.welcomeScreen.customHtml !== undefined) {
                  setWelcomeCustomHtml(schema.welcomeScreen.customHtml);
                }
                if (schema.welcomeScreen.customCss !== undefined) {
                  setWelcomeCustomCss(schema.welcomeScreen.customCss);
                }
              }
              // Restore welcome builder blocks
              if ((schema as any).welcomeBuilderBlocks) {
                setWelcomeBuilderBlocks((schema as any).welcomeBuilderBlocks);
              }
              if ((schema as any).welcomeBuilderTheme) {
                setWelcomeBuilderTheme({
                  ...DEFAULT_FORM_THEME,
                  ...(schema as any).welcomeBuilderTheme
                });
              }
              // Restore auto-estimate preference
              if ((schema as any).welcomeAutoEstimate !== undefined) {
                setWelcomeAutoEstimate((schema as any).welcomeAutoEstimate);
              }
              // Preserve email routing rules from Form Configuration
              if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
                setEmailRoutingRules(schema.emailRoutingRules);
              }
              // Preserve confirmation page language from Form Configuration
              if (schema.confirmationLanguage) {
                setConfirmationLanguage(schema.confirmationLanguage);
              }
              if (record.Builder_Mode === 'builder' && schema.blocks) {
                setBuilderBlocks(schema.blocks || []);
                setBuilderTheme({
                  ...DEFAULT_FORM_THEME,
                  ...(schema.theme || {})
                });
                setBuilderRules(schema.rules || []);
                setEditorMode('builder');
              } else if (schema.blocks) {
                setBuilderBlocks(schema.blocks || []);
                setBuilderTheme({
                  ...DEFAULT_FORM_THEME,
                  ...(schema.theme || {})
                });
                setBuilderRules(schema.rules || []);
              }
            } catch {
              /* ignore parse errors */
            }
          }
          // Fetch linked email template + newsletter data
          setIsLoadingEnrichment(true);
          try {
            const enrichment = await fetchEnrichmentData(id);
            setEnrichmentData(enrichment);
            if (enrichment) {
              toast.info('Linked email template loaded', {
                description: enrichment.emailSubject ? `Template: "${enrichment.emailSubject}"` : 'Template data will be included in embed codes.',
                duration: 3000
              });
            }
          } catch (err) {
            console.error('Failed to load enrichment data:', err);
          } finally {
            setIsLoadingEnrichment(false);
          }
        } else {
          toast.error('Form not found');
          navigate('/forms');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load form', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const openTemplatePicker = async () => {
    setShowTemplatePicker(true);
    setIsLoadingTemplates(true);
    try {
      const all = await formTemplatesLibraryApi.list();
      // Filter by team: show templates belonging to current workspace or with no team (shared)
      const teamFiltered = all.filter(t => !t.Team || t.Team === currentWorkspace.name.replace(/\s*Team\s*$/i, ''));
      setTemplatePickerTemplates(teamFiltered);
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setIsLoadingTemplates(false);
    }
  };
  const applyFormTemplate = (tpl: FormTemplateRecord) => {
    setFormData({
      html: tpl.HTML || '',
      css: tpl.CSS || '',
      js: tpl.JavaScript1 || ''
    });
    if (tpl.Builder_Mode === 'builder' && tpl.Builder_Schema) {
      try {
        const schema: FormBuilderSchema = JSON.parse(tpl.Builder_Schema);
        setBuilderBlocks(schema.blocks || []);
        setBuilderTheme({
          ...DEFAULT_FORM_THEME,
          ...(schema.theme || {})
        });
        setBuilderRules(schema.rules || []);
        setEditorMode('builder');
      } catch {
        /* fallback to code */
      }
    } else if (tpl.Builder_Schema) {
      try {
        const schema: FormBuilderSchema = JSON.parse(tpl.Builder_Schema);
        setBuilderBlocks(schema.blocks || []);
        setBuilderTheme({
          ...DEFAULT_FORM_THEME,
          ...(schema.theme || {})
        });
        setBuilderRules(schema.rules || []);
      } catch {
        /* ignore */
      }
    }
    setTemplateApplied(true);
    setShowTemplatePicker(false);
    toast.success(`Template "${tpl.Template_Name}" applied`, {
      description: 'Set a form name and save when ready.',
      duration: 3000
    });
  };
  const filteredPickerTemplates = templatePickerTemplates.filter(t => {
    if (!templateSearch) return true;
    const q = templateSearch.toLowerCase();
    return (t.Template_Name || '').toLowerCase().includes(q) || (t.Description || '').toLowerCase().includes(q) || (t.Category || '').toLowerCase().includes(q);
  });
  // Helper to build the full welcome config (used in embed generation)
  const getWelcomeConfig = (): WelcomeScreenConfig => ({
    enabled: welcomeScreenEnabled,
    editorMode: welcomeEditorMode,
    title: welcomeTitle,
    description: welcomeDescription,
    buttonText: welcomeButtonText,
    imageUrl: welcomeImageUrl,
    estimatedTime: welcomeEstimatedTime,
    backgroundColor: welcomeBgColor,
    textColor: welcomeTextColor,
    customHtml: welcomeCustomHtml,
    customCss: welcomeCustomCss
  });
  // Helper to generate preview HTML with welcome screen
  const getPreviewHtml = (): {
    html: string;
    css: string;
  } => {
    if (welcomeScreenEnabled) {
      const welcomeConfig = getWelcomeConfig();
      const containerId = 'preview-' + (urlSlug || 'form');
      const fullHtml = generateWelcomeScreenHtml(welcomeConfig, formData.html, containerId);
      return {
        html: fullHtml,
        css: formData.css
      };
    }
    return {
      html: formData.html,
      css: formData.css
    };
  };
  const renderSettingsSidebar = () => <div className="space-y-4" data-id="element-6058" data-mp="g11aeqyyog7y">
      {/* Compact summary card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-6059" data-mp="2fddibkczf9p7">
        <div className="space-y-2" data-id="element-6060" data-mp="2ae0nodfvcyc2">
          <label className="block text-sm font-medium text-gray-700" data-id="element-6061" data-mp="2af2838iryx6g">
            Form Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="My Signup Form" data-id="element-6062" data-mp="2hwqmjb121ax4" />
        </div>

        <div className="flex items-center justify-between gap-3 pt-1" data-id="element-6063" data-mp="2wo93pc5nhbi2">
          <div className="flex items-center gap-3 text-xs text-gray-500 min-w-0" data-id="element-6064" data-mp="1yzyw8hn3hvjk">
            <span className="inline-flex items-center gap-1.5 flex-shrink-0" data-id="element-6065" data-mp="23yjxmke32l9l">
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${status === 'active' ? 'bg-green-500' : status === 'draft' ? 'bg-yellow-500' : 'bg-gray-400'}`} data-id="element-6066" data-mp="28w29aijhjela" />
              <span className="capitalize font-medium text-gray-700" data-id="element-6067" data-mp="3kca3nworjjlv">
                {status}
              </span>
            </span>
            <span className="text-gray-300" data-id="element-6068" data-mp="3oc15cvftvk56">•</span>
            <span className="truncate" data-id="element-6069" data-mp="1p8c3ijs4hrl5">{team}</span>
            <span className="text-gray-300" data-id="element-6070" data-mp="1p711aoebsrw5">•</span>
            <span className="truncate" data-id="element-6071" data-mp="1k9rbh7n0ye7s">{region || 'All regions'}</span>
          </div>
          {enrichmentData && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-50 text-green-700 border border-green-200 flex-shrink-0" data-id="element-6072" data-mp="1y9iob51t1ok7">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6073" data-mp="15kbom2qm6sxd">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-6074" data-mp="1faxzxkrasxa9" />
              </svg>
              Email linked
            </span>}
        </div>

        <button type="button" onClick={() => setShowFormSettings(true)} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors" data-id="element-6075" data-mp="msaiqgffa2eg">
          <Settings className="h-4 w-4 text-gray-500" data-id="element-6076" data-mp="2yadqkwg5tz5e" />
          Form Settings
        </button>
      </div>
    </div>;
  const renderSettingsOverlay = () => showFormSettings ? <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-id="element-6077" data-mp="2phafguszlydx">
        <div className="min-h-screen" data-id="element-6078" data-mp="3d63fb2cqcqdt">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4" data-id="element-6079" data-mp="3d4impi9z7hil">
            <div className="flex items-center justify-between max-w-3xl mx-auto" data-id="element-6080" data-mp="3h5uadi9ahso5">
              <div className="flex items-center gap-4" data-id="element-6081" data-mp="337xfcqahhubl">
                <button onClick={() => setShowFormSettings(false)} className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-6082" data-mp="386h22swwja50">
                  <ArrowLeft className="h-5 w-5" data-id="element-6083" data-mp="1wrts4bk857ok" />
                </button>
                <div data-id="element-6084" data-mp="1wpqqeu9plq40">
                  <h1 className="text-xl font-semibold text-gray-900" data-id="element-6085" data-mp="2c1i29w700w64">
                    Form Settings
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-6086" data-mp="279yxk31y163b">
                    Configure form behavior, messages, and embed codes
                  </p>
                </div>
              </div>
              <button onClick={() => setShowFormSettings(false)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6087" data-mp="1yb4tjrma9dfq">
                <Check className="h-4 w-4" data-id="element-6088" data-mp="2qzsipya5kmyk" /> Done
              </button>
            </div>
          </div>

          {/* Settings Content */}
          <div className="max-w-3xl mx-auto px-6 py-8 space-y-6" data-id="element-6089" data-mp="2h13ayybz0iw1">
            {/* Settings Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-4" data-id="element-6090" data-mp="1akhln6cdant6">
              <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-6091" data-mp="15lxtupb3ehsw">
                General
              </h3>
              <div className="space-y-2" data-id="element-6092" data-mp="vj29povnypoj">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6093" data-mp="10gu2sm81ohub">
                  Form Name
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="My Signup Form" data-id="element-6094" data-mp="15fdtt11hwjtj" />
              </div>
              <div className="space-y-2" data-id="element-6095" data-mp="1j94ynw4a7fix">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6096" data-mp="1o7gaudi1u24b">
                  URL Slug
                </label>
                <div className="flex rounded-md shadow-sm" data-id="element-6097" data-mp="1t4q9mjrbvq4t">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-6098" data-mp="3pyjnf8bi1qri">
                    /forms/
                  </span>
                  <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
                setUrlSlug(e.target.value);
                setSlugManuallyEdited(true);
              }} placeholder="auto-generated-from-name" data-id="element-6099" data-mp="3mvdk86f9jpr9" />
                </div>
                {!slugManuallyEdited && !isEditing && formName && <p className="text-xs text-gray-400" data-id="element-6100" data-mp="jfznb0vt4i0m">
                    Auto-generated from form name
                  </p>}
              </div>
              <div className="space-y-2" data-id="element-6101" data-mp="eipr92dwx83t">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6102" data-mp="sfd2892p9ysq">
                  Status
                </label>
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-6103" data-mp="og464xk6tou0">
                  <option value="active" data-id="element-6104" data-mp="y2yc0o3a4ybn">Active</option>
                  <option value="draft" data-id="element-6105" data-mp="y4hu8k8hxjg2">Draft</option>
                  <option value="archived" data-id="element-6106" data-mp="1307015b3zwkt">Archived</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-6107" data-mp="obk7g1wh18bn">
                <div className="space-y-2" data-id="element-6108" data-mp="t3296s6bjcxo">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-6109" data-mp="t3u6q8jh7kql">
                    Team
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-6110" data-mp="f9kqf9lgi48p" />
                  <p className="text-xs text-gray-400" data-id="element-6111" data-mp="ac1c69ordrgr">
                    From current workspace
                  </p>
                </div>
                <div className="space-y-2" data-id="element-6112" data-mp="kvhmk4bnt3o7">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-6113" data-mp="kwk9y29rwnzd">
                    Region
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={region || '(All regions)'} disabled data-id="element-6114" data-mp="gw0fx7khy0hg" />
                  <p className="text-xs text-gray-400" data-id="element-6115" data-mp="23oq1zxdt0bq">From current scope</p>
                </div>
              </div>
            </div>

            {/* Submission Behavior Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-4" data-id="element-6116" data-mp="3pbae44l9mljy">
              <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-6117" data-mp="3k9bcp0935ypn">
                Submission Behavior
              </h3>
              <div className="space-y-2" data-id="element-6118" data-mp="1utr4ht445z0n">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6119" data-mp="1usphqrdqjo33">
                  Post-Submission Redirect
                </label>
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={postSubmitRedirectUrl} onChange={e => setPostSubmitRedirectUrl(e.target.value)} data-id="element-6120" data-mp="24roscfi29tsq">
                  <option value="" data-id="element-6121" data-mp="1h23wiw0t7221">None (Show Success Message)</option>
                  {landingPages.map(page => <option key={page.ID} value={`/page/${page.Url_Slug}`} data-id="element-6122" data-mp="1l35vrcuq7e40">
                      {page.Page_Name} (/page/{page.Url_Slug})
                    </option>)}
                </select>
                <p className="text-xs text-gray-400" data-id="element-6123" data-mp="tdb77ieozsge">
                  If set, users will be redirected here immediately after
                  submitting the form.
                </p>
              </div>
              <div className="space-y-2" data-id="element-6124" data-mp="2dkslan3iev48">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6125" data-mp="2dltvkpcpbdkw">
                  Success Message
                </label>
                <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={successMessage} onChange={e => setSuccessMessage(e.target.value)} placeholder="Successfully submitted! Please check your email to confirm." disabled={!!postSubmitRedirectUrl} data-id="element-6126" data-mp="2mjxlryymsyj1" />
                <p className="text-xs text-gray-400" data-id="element-6127" data-mp="36aj1r2sm693i">
                  Shown as a popup toast after submission (if no redirect is
                  set).
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-6128" data-mp="3faxn225kvo70">
                <div className="space-y-2" data-id="element-6129" data-mp="3k7eg9armwsqy">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-6130" data-mp="3k63bb7mgdey9">
                    Submitting Button Text
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={submittingText} onChange={e => setSubmittingText(e.target.value)} placeholder="Enviando…" data-id="element-6131" data-mp="3pcryh4rgr5wt" />
                </div>
                <div className="space-y-2" data-id="element-6132" data-mp="bxmyh95dsauh">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-6133" data-mp="byoiw48ae9ov">
                    Error Message
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={errorMessage} onChange={e => setErrorMessage(e.target.value)} placeholder="Error al enviar. Inténtalo de nuevo." data-id="element-6134" data-mp="35tgdia7es2s" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-6135" data-mp="2u2wf3epe7cv4">
                <label className="block text-sm font-medium text-gray-700" data-id="element-6136" data-mp="2u60pittgb4u8">
                  Validation Error Message
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={validationErrorMessage} onChange={e => setValidationErrorMessage(e.target.value)} placeholder="Por favor, complete todos los campos obligatorios." data-id="element-6137" data-mp="2kdcx0b9i7n1b" />
              </div>
            </div>

            {/* Linked Email Template Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-3" data-id="element-6138" data-mp="3qtypud8bg08x">
              <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2" data-id="element-6139" data-mp="3vttd86svu7p8">
                Linked Email Template
              </h3>
              {isLoadingEnrichment ? <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-6140" data-mp="1f7db8k8rcnzl">
                  <Loader2 className="h-4 w-4 animate-spin" data-id="element-6141" data-mp="1tvrcwrufu6ek" />
                  Loading linked template...
                </div> : enrichmentData ? <div className="space-y-2" data-id="element-6142" data-mp="1p3q3ddonmts8">
                  <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-md" data-id="element-6143" data-mp="22w63a5m9ebox">
                    <svg className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6144" data-mp="1yvm30t6e2ryk">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-6145" data-mp="igcedjmkz6hc" />
                    </svg>
                    <div className="text-xs text-green-800" data-id="element-6146" data-mp="ezgbwi4k5bqn">
                      <p className="font-medium" data-id="element-6147" data-mp="f115o5j1sqng">
                        Template linked &amp; baked into embeds
                      </p>
                      {enrichmentData.emailSubject && <p className="mt-0.5 text-green-700" data-id="element-6148" data-mp="ov06n17dy4dt">
                          Subject: {enrichmentData.emailSubject}
                        </p>}
                      {enrichmentData.newsletterId && <p className="text-green-700" data-id="element-6149" data-mp="16qlm1hzxknq">
                          Newsletter ID: {enrichmentData.newsletterId}
                        </p>}
                      {enrichmentData.redirectUrl && <p className="text-green-700" data-id="element-6150" data-mp="aymmfkbgvovn">
                          Redirect: {enrichmentData.redirectUrl}
                        </p>}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-6151" data-mp="3ek23nlo0m92r">
                    Embed codes include confirmation token generation, email
                    template rendering, and all submission fields.
                  </p>
                </div> : <div className="space-y-2" data-id="element-6152" data-mp="1mpat3uelcdtf">
                  <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md" data-id="element-6153" data-mp="1ms6w3x5apsoq">
                    <svg className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6154" data-mp="1cs68w04b2sf9">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" data-id="element-6155" data-mp="13xrr9qwaz5sz" />
                    </svg>
                    <div className="text-xs text-gray-600" data-id="element-6156" data-mp="p4ok8dgc8agi">
                      <p className="font-medium" data-id="element-6157" data-mp="fe7zhnwdfogr">No email template linked</p>
                      <p className="mt-0.5" data-id="element-6158" data-mp="kem04kvn7w4r">
                        Submissions will be saved directly without sending a
                        confirmation email.
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-6159" data-mp="agercpbickdu">
                    To send confirmation emails, link a template in the Form
                    Configurations page.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div> : null;
  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Form Name is required');
      return;
    }
    setIsSaving(true);
    // Use current enrichment data for embed generation
    let currentEnrichment = enrichmentData;
    const welcomeConfig: WelcomeScreenConfig = {
      enabled: welcomeScreenEnabled,
      editorMode: welcomeEditorMode,
      title: welcomeTitle,
      description: welcomeDescription,
      buttonText: welcomeButtonText,
      imageUrl: welcomeImageUrl,
      estimatedTime: welcomeEstimatedTime,
      backgroundColor: welcomeBgColor,
      textColor: welcomeTextColor,
      customHtml: welcomeCustomHtml,
      customCss: welcomeCustomCss
    };
    const jsEmbed = generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, currentEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, builderRules);
    const iframeEmbed = generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, currentEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, builderRules);
    const payload: Record<string, any> = {
      Form_Name: formName,
      Url_Slug: urlSlug,
      Status: status,
      HTML: formData.html,
      CSS: formData.css,
      JavaScript1: formData.js,
      JavaScript_Embed: jsEmbed,
      Iframe_Embed: iframeEmbed,
      Team: team,
      Region: region,
      Builder_Mode: editorMode
    };
    // Always save Builder_Schema to persist successMessage and builder state
    const schemaData: Record<string, any> = {
      successMessage,
      submittingText,
      errorMessage,
      validationErrorMessage,
      postSubmitRedirectUrl,
      welcomeScreen: welcomeConfig,
      welcomeAutoEstimate
    };
    // Preserve confirmation page language set via Form Configuration
    if (confirmationLanguage) {
      schemaData.confirmationLanguage = confirmationLanguage;
    }
    // Preserve email routing rules set via Form Configuration
    if (emailRoutingRules.length > 0) {
      schemaData.emailRoutingRules = emailRoutingRules;
      console.log('[SignupFormEditor] Preserving routing rules in Builder_Schema:', {
        rulesCount: emailRoutingRules.length,
        rules: emailRoutingRules.map(r => ({
          field: r.field,
          op: r.operator,
          value: r.value,
          templateId: r.templateId
        }))
      });
    } else {
      console.log('[SignupFormEditor] No routing rules to preserve (emailRoutingRules state is empty)');
    }
    if (builderBlocks.length > 0 || editorMode === 'builder') {
      schemaData.blocks = builderBlocks;
      schemaData.theme = builderTheme;
      schemaData.rules = builderRules;
    }
    if (welcomeBuilderBlocks.length > 0 || welcomeEditorMode === 'builder') {
      schemaData.welcomeBuilderBlocks = welcomeBuilderBlocks;
      schemaData.welcomeBuilderTheme = welcomeBuilderTheme;
    }
    payload.Builder_Schema = JSON.stringify(schemaData);
    try {
      let savedFormId = id || '';
      if (isEditing && id) {
        await signupFormsApi.update(id, payload);
        savedFormId = id;
        toast.success('Signup form updated', {
          description: '"' + formName + '" has been updated.'
        });
        (async () => {
          try {
            const versions = await formVersionsApi.listByFormId(id);
            // Deactivate all existing versions
            for (const v of versions) {
              if (v.Is_Active === true) {
                try {
                  await formVersionsApi.update(v.ID, {
                    Is_Active: false
                  });
                } catch (e) {
                  console.warn('Failed to deactivate version:', e);
                }
              }
            }
            const nextVersion = versions.length > 0 ? Math.max(...versions.map(v => v.Version_Number || 0)) + 1 : 1;
            await formVersionsApi.create({
              Form_ID: id,
              Form_Name: formName,
              Version_Number: nextVersion,
              HTML: formData.html,
              CSS: formData.css,
              JavaScript1: formData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Updated form',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'updated',
            entityType: 'form',
            entityId: id,
            entityName: formName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
        if (originalHtml && formData.html !== originalHtml) {
          try {
            const impact = await checkVariableImpact(id, originalHtml, formData.html);
            if (impact.affected) {
              toast.warning('Form variables changed — templates may need updating', {
                description: 'Removed fields (' + impact.removedFields.join(', ') + ') are used in: ' + impact.templateSubjects.join(', '),
                duration: 10000,
                action: {
                  label: 'View Configurations',
                  onClick: () => navigate('/configurations')
                }
              });
            } else if (impact.removedFields.length > 0) {
              toast.info('Form fields changed', {
                description: 'Removed: ' + impact.removedFields.join(', ') + '. No linked templates affected.',
                duration: 5000
              });
            }
          } catch (err) {
            console.error('Variable impact check failed:', err);
          }
          setOriginalHtml(formData.html);
        }
      } else {
        const result = await signupFormsApi.create(payload);
        // Extract the new record ID from Creator response
        if (result?.data?.ID) {
          savedFormId = String(result.data.ID);
        }
        toast.success('Signup form created', {
          description: '"' + formName + '" has been saved to Zoho Creator.'
        });
        (async () => {
          try {
            await formVersionsApi.create({
              Form_ID: savedFormId,
              Form_Name: formName,
              Version_Number: 1,
              HTML: formData.html,
              CSS: formData.css,
              JavaScript1: formData.js,
              Builder_Schema: payload.Builder_Schema || '',
              Created_By_Name: currentUser.User_Name,
              Created_By_Email: currentUser.Email,
              Change_Summary: 'Initial version',
              Team: team,
              Region: region,
              Is_Active: true
            });
          } catch (err) {
            console.error('Failed to create form version:', err);
          }
          auditService.log({
            actionType: 'created',
            entityType: 'form',
            entityId: savedFormId,
            entityName: formName,
            userName: currentUser.User_Name,
            userEmail: currentUser.Email,
            userRole: currentUser.Role,
            team: currentWorkspace.name,
            region: currentSubRegion || ''
          });
        })();
      }
      // After saving, try to fetch enrichment data and re-save embed codes if available
      if (savedFormId) {
        try {
          const freshEnrichment = await fetchEnrichmentData(savedFormId);
          if (freshEnrichment) {
            setEnrichmentData(freshEnrichment);
            if (!currentEnrichment || JSON.stringify(freshEnrichment) !== JSON.stringify(currentEnrichment)) {
              const updatedJsEmbed = generateJsEmbed(urlSlug, formData.html, formData.css, formData.js, team, freshEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, builderRules);
              const updatedIframeEmbed = generateIframeEmbed(urlSlug, formData.html, formData.css, formData.js, team, freshEnrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, welcomeConfig, builderRules);
              await signupFormsApi.update(savedFormId, {
                JavaScript_Embed: updatedJsEmbed,
                Iframe_Embed: updatedIframeEmbed
              });
              toast.success('Embed codes enriched with email template data', {
                description: freshEnrichment.emailSubject ? `Linked template: "${freshEnrichment.emailSubject}"` : 'Template data baked into embed codes.',
                duration: 4000
              });
            }
          } else if (!currentEnrichment) {
            toast.info('No email template linked', {
              description: 'Link an email template via Form Configurations to enable confirmation emails in the embed.',
              duration: 5000
            });
          }
        } catch (err) {
          console.error('Post-save enrichment failed:', err);
        }
      }
      if (!isEditing) {
        navigate('/forms');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save signup form', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleRestoreVersion = async (version: any) => {
    try {
      // 1. Update local state with version data
      setFormData({
        html: version.HTML || '',
        css: version.CSS || '',
        js: version.JavaScript1 || ''
      });
      // Try to parse Builder_Schema if it exists
      if (version.Builder_Schema) {
        try {
          const schema = JSON.parse(version.Builder_Schema);
          // Restore builder blocks/theme/rules
          if (schema.blocks) setBuilderBlocks(schema.blocks);
          if (schema.theme) setBuilderTheme(schema.theme);
          if (schema.rules) setBuilderRules(schema.rules);
          // Restore welcome screen blocks/theme
          if (schema.welcomeBuilderBlocks) setWelcomeBuilderBlocks(schema.welcomeBuilderBlocks);
          if (schema.welcomeBuilderTheme) setWelcomeBuilderTheme(schema.welcomeBuilderTheme);
          // Restore other schema settings
          if (schema.successMessage) setSuccessMessage(schema.successMessage);
          if (schema.submittingText) setSubmittingText(schema.submittingText);
          if (schema.errorMessage) setErrorMessage(schema.errorMessage);
          if (schema.validationErrorMessage) setValidationErrorMessage(schema.validationErrorMessage);
          if (schema.postSubmitRedirectUrl) setPostSubmitRedirectUrl(schema.postSubmitRedirectUrl);
          if (schema.confirmationLanguage) setConfirmationLanguage(schema.confirmationLanguage);
          if (schema.emailRoutingRules) setEmailRoutingRules(schema.emailRoutingRules);
          if (schema.welcomeScreen) {
            setWelcomeScreenEnabled(schema.welcomeScreen.enabled ?? false);
            setWelcomeEditorMode(schema.welcomeScreen.editorMode || 'code');
            setWelcomeTitle(schema.welcomeScreen.title || '');
            setWelcomeDescription(schema.welcomeScreen.description || '');
            setWelcomeButtonText(schema.welcomeScreen.buttonText || '');
            setWelcomeImageUrl(schema.welcomeScreen.imageUrl || '');
            setWelcomeEstimatedTime(schema.welcomeScreen.estimatedTime || '');
            setWelcomeBgColor(schema.welcomeScreen.backgroundColor || '');
            setWelcomeTextColor(schema.welcomeScreen.textColor || '');
            setWelcomeCustomHtml(schema.welcomeScreen.customHtml || '');
            setWelcomeCustomCss(schema.welcomeScreen.customCss || '');
          }
          if (schema.welcomeAutoEstimate !== undefined) {
            setWelcomeAutoEstimate(schema.welcomeAutoEstimate);
          }
          // If there are blocks, switch to builder mode
          if (schema.blocks && schema.blocks.length > 0) {
            setEditorMode('builder');
          } else {
            setEditorMode('code');
          }
        } catch (e) {
          console.error('Failed to parse version Builder_Schema:', e);
          setEditorMode('code');
        }
      } else {
        // No schema, must be code mode
        setEditorMode('code');
      }
      // 2. Save the restored version's content to the main form record.
      // This ONLY updates the form record — it does NOT create or modify any version history entries.
      // All existing versions (v1, v2, v3, etc.) remain intact.
      // Determine the correct builder mode from the version data
      const restoredMode = version.Builder_Schema ? (() => {
        try {
          const s = JSON.parse(version.Builder_Schema);
          return s.blocks && s.blocks.length > 0 ? 'builder' : 'code';
        } catch {
          return 'code';
        }
      })() : 'code';
      const payload: Record<string, any> = {
        HTML: version.HTML || '',
        CSS: version.CSS || '',
        JavaScript1: version.JavaScript1 || '',
        Builder_Schema: version.Builder_Schema || '',
        Builder_Mode: restoredMode
      };
      if (id) {
        await signupFormsApi.update(id, payload);
      }
    } catch (err) {
      console.error('Error restoring version:', err);
      throw err;
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-6160" data-mp="fszjnr1uweo4">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-6161" data-mp="ks2buawqmqeo" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-6162" data-mp="kq97j29lki9u">Loading form...</span>
      </div>;
  }
  return <div className="space-y-6 max-w-[1600px] mx-auto pb-20" data-id="element-6163" data-mp="tkw91nc54agt">
      {/* Full-screen Welcome Screen Editor Overlay */}
      {showWelcomeScreenEditor && <div className="fixed inset-0 z-50 bg-white overflow-y-auto" data-id="element-6164" data-mp="ylbhq1lh8tsg">
          <div className="min-h-screen" data-id="element-6165" data-mp="13ik125id33va">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4" data-id="element-6166" data-mp="13gqzh4mlv9u5">
              <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-6167" data-mp="2zjvlu5uuyzxb">
                <div className="flex items-center gap-4" data-id="element-6168" data-mp="2uoycexto2ojw">
                  <button onClick={() => setShowWelcomeScreenEditor(false)} className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-6169" data-mp="39cb7pe9a08nh">
                    <ArrowLeft className="h-5 w-5" data-id="element-6170" data-mp="3iby41ln5eo9o" />
                  </button>
                  <div data-id="element-6171" data-mp="3ebe7okaefool">
                    <h1 className="text-xl font-semibold text-gray-900" data-id="element-6172" data-mp="3ea2whzemjeng">
                      Configure Welcome Screen
                    </h1>
                    <p className="text-sm text-gray-500" data-id="element-6173" data-mp="3s8jcyi2agjis">
                      Design the intro screen visitors see before the form
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-6174" data-mp="1xypg7mfy1uys">
                  <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-6175" data-mp="1sz32zd048epz">
                    <Eye className="h-4 w-4" data-id="element-6176" data-mp="3uvv7k9f6jjx6" /> Preview
                  </button>
                  <button onClick={() => setShowWelcomeScreenEditor(false)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6177" data-mp="3p0rp5ftgaw1">
                    <Check className="h-4 w-4" data-id="element-6178" data-mp="3qvb64tnkpu58" /> Done
                  </button>
                </div>
              </div>
            </div>

            {/* Welcome Screen Editor Content */}
            <div className="max-w-7xl mx-auto px-6 py-8 space-y-6" data-id="element-6179" data-mp="3gwc1dm5xiini">
              {/* Editor Mode Toggle */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5" data-id="element-6180" data-mp="1a5mcql8dzjt3">
                <div className="flex items-center justify-between mb-4" data-id="element-6181" data-mp="1f3nritys4wyr">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-6182" data-mp="3b7ludnvsmqx9">
                    Editor Mode
                  </h3>
                  <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-6183" data-mp="3768m7758abn6">
                    <button type="button" onClick={() => setWelcomeEditorMode('settings')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${welcomeEditorMode === 'settings' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6184" data-mp="3252qr1k6oa9y">
                      Settings
                    </button>
                    <button type="button" onClick={() => setWelcomeEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${welcomeEditorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6185" data-mp="2jge86m96ah1c">
                      Code
                    </button>
                    <button type="button" onClick={() => setWelcomeEditorMode('builder')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${welcomeEditorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6186" data-mp="1uep1d0aaxy6c">
                      Builder
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-6187" data-mp="1gn0lqn0ugrkm">
                  {welcomeEditorMode === 'settings' ? 'Use simple form fields to configure your welcome screen.' : welcomeEditorMode === 'code' ? 'Write custom HTML and CSS with AI assistance.' : 'Drag and drop blocks to build your welcome screen.'}
                </p>
              </div>

              {/* Settings Mode */}
              {welcomeEditorMode === 'settings' && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-5" data-id="element-6188" data-mp="pvbe5gooixxk">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4" data-id="element-6189" data-mp="kwi829bq297f">
                    <div className="space-y-1.5" data-id="element-6190" data-mp="zmft4gecuise">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6191" data-mp="zo0kxy9abnmk">
                        Title
                      </label>
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeTitle} onChange={e => setWelcomeTitle(e.target.value)} placeholder="Tell Everyone!" data-id="element-6192" data-mp="6wpapv5owuqh" />
                    </div>
                    <div className="space-y-1.5" data-id="element-6193" data-mp="1s9ipb1vk6hfh">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6194" data-mp="1nc0m7s482wfe">
                        Button Text
                      </label>
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeButtonText} onChange={e => setWelcomeButtonText(e.target.value)} placeholder="Join Us" data-id="element-6195" data-mp="1utooyxd53lq8" />
                    </div>
                    <div className="sm:col-span-2 space-y-1.5" data-id="element-6196" data-mp="2dnu7bq0aqjl1">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6197" data-mp="2ijj2lh5a9ebj">
                        Description
                      </label>
                      <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeDescription} onChange={e => setWelcomeDescription(e.target.value)} placeholder="The movement is growing every day..." data-id="element-6198" data-mp="28v13r5gkdhaj" />
                    </div>
                    <div className="space-y-1.5" data-id="element-6199" data-mp="27segcrkzi0yc">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6200" data-mp="22umlppsyg0qg">
                        Hero Image URL{' '}
                        <span className="text-gray-400 font-normal" data-id="element-6201" data-mp="1xwuja1uoow80">
                          (Optional)
                        </span>
                      </label>
                      <input type="url" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={welcomeImageUrl} onChange={e => setWelcomeImageUrl(e.target.value)} placeholder="https://..." data-id="element-6202" data-mp="2qkqef24lug2o" />
                    </div>
                    <div className="space-y-1.5" data-id="element-6203" data-mp="156ulso2iilf6">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6204" data-mp="159gvoucvld0k">
                        Estimated Time{' '}
                        <span className="text-gray-400 font-normal" data-id="element-6205" data-mp="3ltaqwjhxol91">
                          (Optional)
                        </span>
                      </label>
                      <div className="flex items-center gap-2 mb-1.5" data-id="element-6206" data-mp="3qqkfxy1euuwc">
                        <button type="button" onClick={() => {
                    const next = !welcomeAutoEstimate;
                    setWelcomeAutoEstimate(next);
                    if (next) {
                      // Recalculate immediately
                      let totalSeconds = 0;
                      if (editorMode === 'builder' && builderBlocks.length > 0) {
                        totalSeconds = calculateEstimatedTimeFromBlocks(builderBlocks);
                      } else if (formData.html) {
                        totalSeconds = calculateEstimatedTimeFromHtml(formData.html);
                      }
                      setWelcomeEstimatedTime(formatEstimatedTime(totalSeconds));
                    }
                  }} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium transition-colors ${welcomeAutoEstimate ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-id="element-6207" data-mp="3vk5dxq307ovj">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6208" data-mp="131u5k65bny18">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-6209" data-mp="3haenedf6swh6" />
                          </svg>
                          {welcomeAutoEstimate ? 'Auto' : 'Manual'}
                        </button>
                        {welcomeAutoEstimate && <span className="text-[11px] text-gray-400" data-id="element-6210" data-mp="2yic04j81ihxy">
                            Calculated from{' '}
                            {editorMode === 'builder' ? `${builderBlocks.filter(b => !LAYOUT_BLOCK_TYPES.has(b.type)).length} fields` : 'form HTML'}
                          </span>}
                      </div>
                      <input type="text" className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${welcomeAutoEstimate ? 'bg-gray-50 text-gray-500' : ''}`} value={welcomeEstimatedTime} onChange={e => {
                  if (!welcomeAutoEstimate) {
                    setWelcomeEstimatedTime(e.target.value);
                  }
                }} readOnly={welcomeAutoEstimate} placeholder={welcomeAutoEstimate ? 'Add form fields to calculate' : '2 minutes'} data-id="element-6211" data-mp="2fs49v1pfvlz6" />
                      {welcomeAutoEstimate && welcomeEstimatedTime && <p className="text-[10px] text-green-600 flex items-center gap-1" data-id="element-6212" data-mp="1q6uu55zfakit">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6213" data-mp="1q8phss22wqql">
                            <circle cx="12" cy="12" r="10" data-id="element-6214" data-mp="30x0yfkjl8uzt" />
                            <polyline points="12 6 12 12 16 14" data-id="element-6215" data-mp="2vyzdsv6iknm8" />
                          </svg>
                          Auto-calculated: {welcomeEstimatedTime}
                        </p>}
                    </div>
                    <div className="space-y-1.5" data-id="element-6216" data-mp="2n1ff9u01d5fb">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6217" data-mp="2hzgcomc4ecjf">
                        Background Color
                      </label>
                      <div className="flex items-center gap-2" data-id="element-6218" data-mp="2d3hpv36fx2l4">
                        <input type="color" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-6219" data-mp="g7jwgzf0ceib" />
                        <input type="text" value={welcomeBgColor} onChange={e => setWelcomeBgColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-6220" data-mp="3918zzi7me81e" />
                        <div className="h-6 w-12 rounded" style={{
                    backgroundColor: welcomeBgColor
                  }} data-id="element-6221" data-mp="2q5sfeu6o2ahl" />
                      </div>
                    </div>
                    <div className="space-y-1.5" data-id="element-6222" data-mp="22g7xmem91j9w">
                      <label className="block text-sm font-medium text-gray-700" data-id="element-6223" data-mp="27dq6y646i0if">
                        Text Color
                      </label>
                      <div className="flex items-center gap-2" data-id="element-6224" data-mp="1nszhr0hzwprl">
                        <input type="color" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-6225" data-mp="1jr4dbn4rpw9l" />
                        <input type="text" value={welcomeTextColor} onChange={e => setWelcomeTextColor(e.target.value)} className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono uppercase" data-id="element-6226" data-mp="26pr3dgl0fgyt" />
                        <div className="h-6 w-12 rounded border border-gray-200" style={{
                    backgroundColor: welcomeTextColor
                  }} data-id="element-6227" data-mp="2qg4970a980bs" />
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Code Mode */}
              {welcomeEditorMode === 'code' && <div className="space-y-4" data-id="element-6228" data-mp="3782gfob6gkqu">
                  <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-6229" data-mp="37942entqernc">
                    <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between" data-id="element-6230" data-mp="3c6ds84l195bp">
                      <div className="flex items-center gap-2" data-id="element-6231" data-mp="3g75zisach05c">
                        <span className="text-sm font-medium text-gray-900" data-id="element-6232" data-mp="3l7buahbwv2rn">
                          Welcome Screen — Code Editor
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-800" data-id="element-6233" data-mp="3q4kiayjqq10t">
                          Custom
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-6234" data-mp="1o9whmk2krutw">
                        {(['html', 'css'] as const).map(tab => <button key={tab} onClick={() => setWelcomeActiveTab(tab)} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${welcomeActiveTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-6235" data-mp="1jh3bpsvdegg8">
                            {tab.toUpperCase()}
                          </button>)}
                      </div>
                    </div>
                    <div className="p-4" data-id="element-6236" data-mp="1xc5pf26gla3l">
                      {welcomeActiveTab === 'html' && <CodeEditor label="Welcome Screen HTML" language="html" placeholder={'<div class="welcome">\n  <h1>Welcome!</h1>\n  <p>Description text here</p>\n  <button class="welcome-start-btn">Get Started</button>\n</div>'} value={welcomeCustomHtml} onChange={e => setWelcomeCustomHtml(e.target.value)} data-id="element-6237" data-mp="1sd2mpodybfys" />}
                      {welcomeActiveTab === 'css' && <CodeEditor label="Welcome Screen CSS" language="css" placeholder={'.welcome { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; }'} value={welcomeCustomCss} onChange={e => setWelcomeCustomCss(e.target.value)} data-id="element-6238" data-mp="11moskhhqzomd" />}
                    </div>
                    <div className="px-4 pb-3 text-[10px] text-gray-400" data-id="element-6239" data-mp="2d0u1lowtiook">
                      Tip: Add{' '}
                      <code className="bg-gray-100 px-1 rounded" data-id="element-6240" data-mp="1jh0xrvb21e3">
                        class="welcome-start-btn"
                      </code>{' '}
                      or{' '}
                      <code className="bg-gray-100 px-1 rounded" data-id="element-6241" data-mp="b9tidb7tpeg0">
                        data-action="start"
                      </code>{' '}
                      to any button to make it transition to the form.
                    </div>
                  </div>
                  <GptCodeAssistant mode="form" currentCode={{
              html: welcomeCustomHtml,
              css: welcomeCustomCss,
              js: ''
            }} onCodeGenerated={code => {
              if (code.html) setWelcomeCustomHtml(code.html);
              if (code.css !== undefined) setWelcomeCustomCss(code.css || '');
            }} data-id="element-6242" data-mp="39sie9unyj2id" />
                </div>}

              {/* Builder Mode */}
              {welcomeEditorMode === 'builder' && <div className="space-y-2" data-id="element-6243" data-mp="1sqvvbuk4qkck">
                  <FormBuilder initialBlocks={welcomeBuilderBlocks} initialTheme={welcomeBuilderTheme} customPalette={LANDING_PAGE_PALETTE} hideLogicRules onSchemaChange={(blocks, theme) => {
              setWelcomeBuilderBlocks(blocks);
              setWelcomeBuilderTheme(theme);
            }} onHtmlChange={(html, css) => {
              setWelcomeCustomHtml(html);
              setWelcomeCustomCss(css);
            }} data-id="element-6244" data-mp="1srx4tulhyys6" />
                </div>}
            </div>
          </div>
        </div>}

      {/* Full-screen Form Settings Overlay */}
      {renderSettingsOverlay()}

      <div className="flex items-center justify-between" data-id="element-6245" data-mp="2uctmevyxg6nn">
        <div className="flex items-center gap-4" data-id="element-6246" data-mp="3e1rgfylp9ty9">
          <Link to="/forms" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-6247" data-mp="3dyvi495xx39a">
            <ArrowLeft className="h-5 w-5" data-id="element-6248" data-mp="3431w7z6bzxeu" />
          </Link>
          <div data-id="element-6249" data-mp="344mrxs4dtmea">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6250" data-mp="391mxm1xbkn4v">
              {isEditing ? 'Edit Form: ' + (formName || '...') : 'Create New Form'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-6251" data-mp="300yit503qiqx">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your form.' : 'Configure the raw HTML, CSS, and JS for this form.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-6252" data-mp="2qbnfy03t6abh">
          {/* Embed Code Link — only for saved forms */}
          {isEditing && id && <Link to={`/forms/${id}/embed`} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-6253" data-mp="2gcps6ze7iktj">
              <Code className="h-4 w-4" data-id="element-6254" data-mp="i2h4l99y3n20" /> Embed Code
            </Link>}
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-6255" data-mp="d576744kw121">
            <button onClick={() => {
            if (editorMode === 'builder' && builderBlocks.length > 0) {
              // Builder → Code: HTML/CSS already synced
            }
            setEditorMode('code');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6256" data-mp="86nlf71q0vb9">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && formData.html && builderBlocks.length === 0) {
              setShowBuilderConfirm(true);
              return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6257" data-mp="3gm79zsrg5pjz">
              Builder
            </button>
          </div>

          {/* View Code Button - Only in Builder mode */}
          {editorMode === 'builder' && <button onClick={() => setShowCodeModal(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors" title="View Generated HTML" data-id="element-6258" data-mp="2fprtegvl3lhm">
              <Code className="h-4 w-4" data-id="element-6259" data-mp="pg828wt4dlby" />
              <span className="hidden sm:inline" data-id="element-6260" data-mp="udhxit36wr7p">View Code</span>
            </button>}

          {/* History Button - Only for saved forms */}
          {isEditing && id && <button onClick={() => setShowVersionHistory(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors" title="Version History" data-id="element-6261" data-mp="3vasaqmfshr89">
              <HistoryIcon className="h-4 w-4" data-id="element-6262" data-mp="3ci7zsk01q6sq" />
            </button>}

          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-6263" data-mp="3cg4yv4xcut98">
            <Eye className="h-4 w-4" data-id="element-6264" data-mp="3hhat576nyokv" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-6265" data-mp="3hfq23rjk5nrr">
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-6266" data-mp="148rpvh72o41y" /> : <Save className="h-4 w-4" data-id="element-6267" data-mp="192cqzi3ype5d" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {/* Template Picker Banner — only for new forms, before a template is applied */}
      {!isEditing && !templateApplied && !formData.html && <div className="bg-white border border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-between" data-id="element-6268" data-mp="1n25hbcbktsct">
          <div className="flex items-center gap-3" data-id="element-6269" data-mp="1e0o1lv22zson">
            <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-6270" data-mp="1dzw5mj4knsxu">
              <LayoutTemplateIcon className="h-5 w-5 text-indigo-600" data-id="element-6271" data-mp="1i1zjtfimlbmt" />
            </div>
            <div data-id="element-6272" data-mp="2limfrkrk1o5">
              <p className="text-sm font-medium text-gray-900" data-id="element-6273" data-mp="2kzcoy59ojl7">
                Start from a template
              </p>
              <p className="text-xs text-gray-500" data-id="element-6274" data-mp="6o5entlcjolv">
                Pre-fill this form with a saved template from your library.
              </p>
            </div>
          </div>
          <button onClick={openTemplatePicker} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 whitespace-nowrap" data-id="element-6275" data-mp="24a4ywz5pub9b">
            Browse Templates
          </button>
        </div>}

      {/* Welcome Screen — Simplified toggle with Configure button */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-6276" data-mp="1qgx7b7hmxfrm">
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-gray-100 bg-gray-50/60" data-id="element-6277" data-mp="1lmjhdklqs0uc">
          <div className="flex items-center gap-3" data-id="element-6278" data-mp="1bm0qw6zo9m3g">
            <div className="h-7 w-7 rounded-md bg-indigo-50 flex items-center justify-center" data-id="element-6279" data-mp="1bnao6typaq91">
              <svg className="h-4 w-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-6280" data-mp="3axts6fthtdlz">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-6281" data-mp="3egslu4oen7iz" />
              </svg>
            </div>
            <div data-id="element-6282" data-mp="3sajwjgdvgxga">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-6283" data-mp="2vi6kcooz7eej">
                Welcome Screen
              </h3>
              <p className="text-xs text-gray-500" data-id="element-6284" data-mp="2zj8it3b2jmfg">
                Shown before the form — a landing intro for visitors
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-6285" data-mp="34l7ju6jc6798">
            {welcomeScreenEnabled && <button type="button" onClick={() => setShowWelcomeScreenEditor(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors" data-id="element-6286" data-mp="39flc402pby9o">
                <Settings className="h-3.5 w-3.5" data-id="element-6287" data-mp="15u62ad292jwo" />
                Configure
              </button>}
            <button type="button" onClick={() => setWelcomeScreenEnabled(!welcomeScreenEnabled)} className={`${welcomeScreenEnabled ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`} data-id="element-6288" data-mp="1jlavoxf0yfyk">
              <span className={`${welcomeScreenEnabled ? 'translate-x-4' : 'translate-x-0'} pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`} data-id="element-6289" data-mp="22kgf3u43sxh1" />
            </button>
          </div>
        </div>

        {/* Show summary when enabled but not editing */}
        {welcomeScreenEnabled && <div className="px-5 py-3 bg-indigo-50/30 border-t border-indigo-100" data-id="element-6290" data-mp="39p42wzvdz0t">
            <div className="flex items-center justify-between" data-id="element-6291" data-mp="2evg15zz0rw0o">
              <div className="text-xs text-gray-600" data-id="element-6292" data-mp="2jspo98yxrvlc">
                <span className="font-medium" data-id="element-6293" data-mp="2jvlr9bpn5agn">Mode:</span>{' '}
                {welcomeEditorMode === 'settings' ? 'Settings' : welcomeEditorMode === 'code' ? 'Custom Code' : 'Builder'}
                {welcomeEditorMode === 'settings' && welcomeTitle && <span className="ml-3" data-id="element-6294" data-mp="213je4ac7kcb5">
                    <span className="font-medium" data-id="element-6295" data-mp="214koeclegurt">Title:</span> {welcomeTitle}
                  </span>}
              </div>
              <button type="button" onClick={() => setShowWelcomeScreenEditor(true)} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-6296" data-mp="1r4tyko7sb3y2">
                Edit →
              </button>
            </div>
          </div>}
      </div>

      {editorMode === 'builder' ? <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} initialRules={builderRules} onSchemaChange={(blocks, theme, rules) => {
      setBuilderBlocks(blocks);
      setBuilderTheme(theme);
      setBuilderRules(rules);
    }} onHtmlChange={(html, css) => {
      setFormData(prev => ({
        ...prev,
        html,
        css
      }));
    }} settingsSidebar={renderSettingsSidebar()} data-id="element-6297" data-mp="s4patbx6a8ag" /> : <div className="grid grid-cols-3 gap-6" data-id="element-6298" data-mp="1kra3cfpdwqfl">
          <div className="col-span-2 space-y-6" data-id="element-6299" data-mp="1kpfh8y2dms9x">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-6300" data-mp="1otetv5kjd6y0">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-6301" data-mp="3mj6dplztfu8s">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={'text-sm font-medium px-3 py-1.5 rounded-md transition-colors ' + (activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')} data-id="element-6302" data-mp="3qkrxlrarp8sh">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-6303" data-mp="v48puocgs2x">
                {activeTab === 'html' && <CodeEditor label="Form HTML" language="html" placeholder="<form>...</form>" value={formData.html} onChange={e => setFormData({
              ...formData,
              html: e.target.value
            })} data-id="element-6304" data-mp="3syncq4jkxhne" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".signup-form { ... }" value={formData.css} onChange={e => setFormData({
              ...formData,
              css: e.target.value
            })} data-id="element-6305" data-mp="2j15dwr3jcrkc" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="document.querySelector..." value={formData.js} onChange={e => setFormData({
              ...formData,
              js: e.target.value
            })} data-id="element-6306" data-mp="204e0gyaod6o7" />}
              </div>
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="form" currentCode={formData} onCodeGenerated={code => {
          setFormData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-6307" data-mp="sr6rndp3vvvy" />
          </div>

          <div className="space-y-6" data-id="element-6308" data-mp="1borhamzp2yhm">{renderSettingsSidebar()}</div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Form Preview" data-id="element-6309" data-mp="1qeyog6k94v30">
        <FormPreview html={formData.html} css={formData.css} js={formData.js} data-id="element-6310" data-mp="1lgdyr12mltst" />
      </PreviewModal>

      <ViewCodeModal isOpen={showCodeModal} onClose={() => setShowCodeModal(false)} html={formData.html} data-id="element-6311" data-mp="1zfn393hgyzgy" />

      <ConfirmModal isOpen={showBuilderConfirm} title="Switch to Builder Mode?" message="The builder starts with a blank canvas — your existing code cannot be imported into the visual builder. However, your code is preserved and will still be there if you switch back to Code mode without saving." confirmLabel="Switch to Builder" variant="warning" onCancel={() => setShowBuilderConfirm(false)} onConfirm={() => {
      setShowBuilderConfirm(false);
      setEditorMode('builder');
    }} data-id="element-6312" data-mp="3owqzinecaeia" />

      {/* Version History Panel */}
      {isEditing && id && <VersionHistoryPanel isOpen={showVersionHistory} onClose={() => setShowVersionHistory(false)} entityType="form" entityId={id} entityName={formName} onRestore={handleRestoreVersion} data-id="element-6313" data-mp="3ex9v09atq6nw" />}
    </div>;
}